// 城市地图:等距视角(斜 45 度)的城,每块地上画它此刻真实的样子。见 12-城市界面.md、11-美术.md。
// 拖动平移、滚轮或双指缩放、点一块地选中。
import type { City, Plot, SubwayLine } from './api.ts';
import { colors, drawGrain, drawRain, drawSky, facade, fitCanvas, hash, skyAt, type Sky } from './art.ts';

const pick = <T,>(xs: T[], r: number): T => xs[Math.min(xs.length - 1, Math.floor(r * xs.length))];

/** 地铁线的颜色(第 k 号线,从 1 起):平常的地图上压着(和地面、楼一个调子);地铁图上饱和一点,同一个色相。 */
const LINE_MUTED = ['#5f7d95', '#8a5d6b', '#6f8a5a', '#8a7a4f', '#6b6590', '#4f8a86', '#7d6450', '#6f7a8a', '#8a8450', '#8f6a50'];
const LINE_VIVID = ['#3f74a3', '#a64f77', '#4f8a4f', '#c08a2e', '#6a5aa6', '#2e8c88', '#8a5a3c', '#5c6f8f', '#8f8a2e', '#b06a3a'];
export const lineColor = (k: number, vivid = true) => (vivid ? LINE_VIVID : LINE_MUTED)[(Math.max(1, k) - 1) % LINE_VIVID.length];
/** 一条线上通了车的那一段(线路的第几格到第几格);还没通车的线、延长段在外面。没有通车的站是 null。 */
const openSpan = (l: SubwayLine): [number, number] | null => {
  if (!l.open) return null;
  const at = l.st.map(([x, y]) => l.path.findIndex(([px, py]) => px === x && py === y));
  const on = at.filter((p, k) => p >= 0 && (l.so ? l.so[k] : true));
  return on.length ? [Math.min(...on), Math.max(...on)] : null;
};
/** 夜里的地铁图:线的颜色往白里调两成,压暗的底上看得清。 */
const lighten = (hex: string, t: number) => {
  const n = parseInt(hex.slice(1), 16);
  const c = [n >> 16, (n >> 8) & 255, n & 255].map((v) => Math.round(v + (255 - v) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
};

const TW = 72; // 地块菱形的宽
const TH = 36; // 地块菱形的高
const FH = 4.2; // 地图上一层楼画多高
/** 挡住鼠标停的、选中的那块地的楼画多淡:看得清后面那块地,也还看得出前面有栋楼。 */
const GHOST = 0.3;
/** 一栋楼盖住那块地的几成才画淡:旁边的楼只遮住一个角(一成多)的不算,不然停到哪都是一片半透明。 */
const GHOST_SHARE = 0.15;

/** 多边形(地面坐标,[x0, y0, x1, y1, ...])落在单位方格 [0, 1]² 里的面积:逐条边裁掉方格外的部分,再按鞋带公式算。 */
function areaInCell(pts: number[]): number {
  let poly = pts;
  // 四条边:x ≥ 0、x ≤ 1、y ≥ 0、y ≤ 1(axis 0 是 x,1 是 y;sign 1 是 ≥ lim,-1 是 ≤ lim)
  for (const [axis, lim, sign] of [[0, 0, 1], [0, 1, -1], [1, 0, 1], [1, 1, -1]]) {
    const out: number[] = [];
    const n = poly.length / 2;
    for (let k = 0; k < n; k++) {
      const ax = poly[k * 2], ay = poly[k * 2 + 1];
      const j = (k + 1) % n;
      const bx = poly[j * 2], by = poly[j * 2 + 1];
      const da = ((axis ? ay : ax) - lim) * sign, db = ((axis ? by : bx) - lim) * sign;
      if (da >= 0) out.push(ax, ay);
      if (da >= 0 !== db >= 0) {
        const t = da / (da - db);
        out.push(ax + (bx - ax) * t, ay + (by - ay) * t);
      }
    }
    poly = out;
    if (poly.length < 6) return 0;
  }
  let s = 0;
  const n = poly.length / 2;
  for (let k = 0; k < n; k++) {
    const j = (k + 1) % n;
    s += poly[k * 2] * poly[j * 2 + 1] - poly[j * 2] * poly[k * 2 + 1];
  }
  return Math.abs(s) / 2;
}
/**
 * 矮楼那一档一栋楼画几层:四层以下照画,再高的按开平方往下压(十层画六层、三十二层画九层、六十层画十三层)。
 * 高低还分得出,又不至于一栋高楼把后面几排都盖住。
 */
const lowFloors = (n: number) => (n <= 4 ? n : Math.round(4 + 1.5 * (Math.sqrt(n) - 2)));
/** 矮楼那一档画的样子:层数换成压低以后的,自己画的楼每层多宽按比例取。 */
const lowPlot = (p: Plot): Plot => {
  if (p.floors <= 4) return p;
  const n = lowFloors(p.floors);
  const w = p.widths;
  return { ...p, floors: n, widths: w?.length ? Array.from({ length: n }, (_, f) => w[Math.min(w.length - 1, Math.floor((f * w.length) / n))]) : w };
};
/** 盖好的住宅、高层、写字楼的墙色(按楼号挑一种);平面那一档楼顶也用它。 */
const WALLS: Record<string, string[]> = {
  housing: ['#ece6da', '#efe3cf', '#e7ddd0', '#f1e9dc', '#e4d8c6', '#ead6c2', '#dfe0d4', '#e6d2bb', '#dcd5ca'],
  highrise: ['#cfd4d8', '#d8d2c8', '#c9cfd2', '#dcd6cd', '#d4cfc4', '#c7ccc9'],
  office: ['#8ea3b3', '#879cad', '#93a6b2'],
  landmark: ['#34404d', '#2f3a46'],
};
/** 双坡顶的瓦色、洋房四坡顶的瓦色(按楼号挑)。 */
const ROOFS = ['#b5553d', '#9a5a3c', '#6f7a80', '#a8483a', '#8a6a4a'];
const VILLA_ROOFS = ['#b5553d', '#9a5a3c', '#6f7a80', '#a8483a'];

const ground: Record<string, [string, string]> = {
  core: ['#aa9c84', '#a29480'], // 市中心:深一档的暖石铺装,和市区的浅灰分得开
  commercial: ['#c2bdb2', '#b8b2a6'],
  residential: ['#a9c29a', '#9fba8f'],
  suburb: ['#cdb98f', '#c3ae84'],
  wild: ['#d6c9a2', '#cec09a'],
  river: ['#cfc6b2', '#c9c0ab'], // 江边的地:沿江步道
};
/** 郊区的田:几种庄稼和休耕地的颜色,一块一块拼起来(不是棋盘格)。 */
const FIELDS = ['#cdb98f', '#c4ae82', '#c6b187', '#b9bd8b', '#bca77c', '#c9b48a'];
const water = ['#7fb1d1', '#7cafcf'];

// 街道:地块之间的一条条路。横竖各按固定的间隔(三格、两格一条,每六格一条大街);
// 只有路边至少一块地有人用(城区、盖了楼)才修,城往外长,路跟着往外铺。
const URBAN = new Set(['core', 'commercial', 'residential', 'river']);
const STREET_X = 3;
const STREET_Y = 2;
const AVENUE = 6;
const ST = 0.055; // 小街每边占地块的宽(一格的几分之几)
const AV = 0.085; // 大街
const CURB = 0.028; // 人行道
const LOT = 0.012; // 同一个街坊里地块之间的缝
const ASPHALT = '#7f796f';
const SIDEWALK = '#d6cebf';
/** 街上跑的车、停车场里停的车:几种常见的车漆,压着饱和度(11-美术.md 限定的颜色里挑)。 */
const CARS = ['#e8e2d6', '#4a4845', '#b5553d', '#3f6e8c', '#c9b27a', '#8f8a82', '#6f8a5a', '#d9d4cb', '#8a5d6b', '#2f3a46'];
/** 每段街上平均几辆车:市中心最多,郊区最少;大街再多一些。 */
const TRAFFIC: Record<string, number> = { core: 1.1, commercial: 0.95, residential: 0.5, river: 0.55, suburb: 0.22, wild: 0.12 };
/** 店屋的雨篷:红白、蓝白、绿白、橙白条纹。 */
const AWNINGS = ['#b5553d', '#3f6e8c', '#4f8a5b', '#d08a3a', '#8a5d6b'];
/** 一块地四边的路:每边占掉多宽(0 是不临街)。paved:这块地有人用(铺装、修路)。 */
interface Lot {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  paved: boolean;
}

let lampSprite: HTMLCanvasElement | null = null;
/** 路灯照在地上的一圈暖光(先画好一张,夜里每盏灯贴一次)。 */
function lamp(): HTMLCanvasElement {
  if (lampSprite) return lampSprite;
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const x = c.getContext('2d')!;
  const gr = x.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, 'rgba(255,214,140,0.85)');
  gr.addColorStop(0.35, 'rgba(255,196,110,0.35)');
  gr.addColorStop(1, 'rgba(255,190,100,0)');
  x.fillStyle = gr;
  x.fillRect(0, 0, 64, 64);
  lampSprite = c;
  return c;
}

interface Cam {
  x: number;
  y: number;
  s: number;
}

export class CityMap {
  private c: HTMLCanvasElement;
  private g: CanvasRenderingContext2D;
  private city: City | null = null;
  private cam: Cam = { x: 0, y: 0, s: 1 };
  private fitted = false;
  private selected: number | null = null;
  private raf = 0;
  private pointers = new Map<number, { x: number; y: number }>();
  private dragged = 0;
  private pinch = 0;
  private onSelect: (p: Plot | null) => void;
  /** 鼠标停在哪块地上(不按键移动时);传给外面画浮动的说明。 */
  onHover: ((p: Plot | null, x: number, y: number) => void) | null = null;
  private hovered: number | null = null;
  /** 街上的车、江上的船走不走(城市暂停时停下);ct 是它们自己的钟(秒)。 */
  private moving = true;
  private ct = 0;
  private lastT = 0;
  setMotion(on: boolean) {
    if (on !== this.moving) this.dirty = true;
    this.moving = on;
  }
  /**
   * 要不要重画:车船在动、镜头在飞、框子在闪的时候每帧都画;都停着的时候只在有变化时画一次
   * (城市换了一天、换天色、拖动缩放、鼠标换了一块地、选中、换地图模式、窗口大小变了)。暂停时就不占电脑了。
   */
  private dirty = true;
  /** 要马上画的(拖动、缩放、鼠标换了一块地、选中、换地图模式);别的变化(城市过了一天、车流)慢一点画也看不出来。 */
  private urgent = true;
  /** 画布像素密度的上限(见 setQuality)。 */
  private cap = Infinity;
  /** 游戏在走的时候画布少画一些像素(Retina 屏两倍密度一帧六百多万个点);暂停下来再按满密度画一张清楚的。 */
  setQuality(cap: number) {
    if (cap === this.cap) return;
    this.cap = cap;
    this.dirty = this.urgent = true;
  }
  private lastW = 0;
  private lastH = 0;
  private lastDraw = 0;
  /** 查性能:画一张用了多久(毫秒,累计)、画了几张(网址带 perf=1 时 sandbox.ts 每两秒打一次)。 */
  drawMs = 0;
  draws = 0;
  private measureDraws = false;
  private drawStages = { frames: 0, groundMs: 0, shadowMs: 0, buildingsMs: 0, dynamicMs: 0, otherMs: 0 };
  private shadowStats = { hits: 0, builds: 0 };
  private listStats = { hits: 0, builds: 0, visibilityTests: 0, groundTests: 0 };
  /** 细分计时只供perf诊断;关闭时不在每块地上读取时钟。 */
  setDrawTiming(on: boolean) { this.measureDraws = on; }
  takeDrawStats() {
    const out = { ...this.drawStages, shadowHits: this.shadowStats.hits, shadowBuilds: this.shadowStats.builds,
      visibleListHits: this.listStats.hits, visibleListBuilds: this.listStats.builds, visibilityTests: this.listStats.visibilityTests, groundTests: this.listStats.groundTests };
    this.drawStages = { frames: 0, groundMs: 0, shadowMs: 0, buildingsMs: 0, dynamicMs: 0, otherMs: 0 };
    this.shadowStats = { hits: 0, builds: 0 };
    this.listStats = { hits: 0, builds: 0, visibilityTests: 0, groundTests: 0 };
    return out;
  }
  /** 模拟里真实的车流(每块地一天过多少辆车)和人流(一天走过、待着多少人),按地块号;没有就按片区估。 */
  private cars: (number | null)[] | null = null;
  private people: (number | null)[] | null = null;
  setActivity(cars: (number | null)[] | null, people: (number | null)[] | null) {
    this.cars = cars;
    this.people = people;
    this.dirty = true;
  }

  constructor(canvas: HTMLCanvasElement, onSelect: (p: Plot | null) => void) {
    this.c = canvas;
    this.g = canvas.getContext('2d')!;
    this.onSelect = onSelect;
    canvas.addEventListener('pointerdown', this.down);
    canvas.addEventListener('pointermove', this.move);
    canvas.addEventListener('pointerup', this.up);
    canvas.addEventListener('pointercancel', this.up);
    canvas.addEventListener('pointerleave', this.leave);
    canvas.addEventListener('wheel', this.wheel, { passive: false });
    document.addEventListener('visibilitychange', this.visibility);
    this.raf = requestAnimationFrame(this.frame);
  }

  /** 鼠标停着的那块地(这座城眼下的样子)。 */
  hoveredPlot(): Plot | null {
    return this.hovered == null ? null : (this.city?.plots.find((p) => p.id === this.hovered) ?? null);
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    document.removeEventListener('visibilitychange', this.visibility);
    this.viewLists = null;
  }

  /** 后台不画看不到的地图；恢复时即使城市仍暂停也立刻按当前镜头/天色重画。 */
  private visibility = () => { this.dirty = this.urgent = true; };

  /** 城市这一刻的样子(同一个对象改了钟点也要再传一次,好重画)。 */
  set(city: City) {
    this.city = city;
    // 同一个对象也可能原地更新了楼高/名牌,不能只按City引用判断。
    this.viewLists = null;
    this.dirty = true;
  }

  /** 地图模式:按地块号给一个颜色(null 不上色);楼画淡一点,让颜色看得见。传 null 回到平常的样子。 */
  private overlay: (string | null)[] | null = null;
  setOverlay(colors: (string | null)[] | null) {
    this.overlay = colors;
    this.dirty = this.urgent = true;
  }

  /** 地铁图层:0 普通(地图上画线和站)、1 地铁图(底图压淡,线和站名看得清)、2 不画。 */
  private transit = 0;
  setTransit(m: number) {
    if (m === this.transit) return;
    this.transit = m;
    this.dirty = this.urgent = true;
  }

  /** 楼高:0 立体(按真实层数画高)、1 矮楼(高楼压低,见 lowFloors)、2 平面(楼不立起来,只画占地,见 footprint)。 */
  private relief = 0;
  setRelief(m: number) {
    if (m === this.relief) return;
    this.relief = m;
    this.dirty = this.urgent = true;
  }

  select(id: number | null) {
    this.selected = id;
    this.dirty = this.urgent = true;
  }

  /** 玩家模式:地图上标出「我」住哪、在哪上班(虚线框加一个小牌子)。 */
  private marks: { plot: number; label: string; color: string; dash?: boolean }[] = [];
  /** label 是空的只描边(名下的房、地、公司按颜色分),有字的再挂一个小牌子(家、上班、有人想买)。 */
  setMarks(marks: { plot: number; label: string; color: string; dash?: boolean }[]) {
    if (JSON.stringify(marks) === JSON.stringify(this.marks)) return;
    this.marks = marks;
    this.dirty = this.urgent = true;
  }
  private drawMarks(city: City) {
    if (!this.marks.length) return;
    const g = this.g;
    const s = this.cam.s;
    // 同一块地上几个牌子往上叠
    const stacked = new Map<number, number>();
    for (const m of this.marks) {
      const p = city.plots.find((q) => q.id === m.plot);
      if (!p) continue;
      g.save();
      g.translate(this.cam.x, this.cam.y);
      g.scale(s, s);
      this.diamond(p.x, p.y, m.label ? 0.08 : 0.14);
      g.setLineDash(m.dash === false ? [] : [5 / s, 3 / s]);
      g.strokeStyle = m.color;
      g.lineWidth = (m.label ? 2.2 : 1.8) / s;
      g.stroke();
      g.restore();
      if (!m.label) continue;
      const [wx, wy] = this.iso(p.x + 0.5, p.y + 0.5);
      const h = this.relief === 2 ? 0 : this.bulk(this.relief === 1 ? lowPlot(p) : p)[1];
      const k = stacked.get(p.id) ?? 0;
      stacked.set(p.id, k + 1);
      const x = wx * s + this.cam.x, y = (wy - h) * s + this.cam.y - 10 - k * 22;
      g.save();
      g.font = '600 12px system-ui, -apple-system, "PingFang SC", sans-serif';
      const w = g.measureText(m.label).width + 14;
      g.fillStyle = m.color;
      g.beginPath();
      g.roundRect(x - w / 2, y - 18, w, 18, 4);
      g.moveTo(x - 4, y);
      g.lineTo(x, y + 5);
      g.lineTo(x + 4, y);
      g.fill();
      g.fillStyle = '#fff';
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText(m.label, x, y - 9);
      g.restore();
    }
  }

  /** 镜头飞到这块地(放大到看得清,摆在画面左边一点,右边留给面板),框子闪两秒。地图大了用它找东西。 */
  private glide: { from: Cam; to: Cam; t0: number } | null = null;
  private pulse = -1e9;
  focus(id: number) {
    const p = this.city?.plots.find((q) => q.id === id);
    if (!p) return;
    this.selected = id;
    const s = Math.max(this.cam.s, 1.4);
    const [wx, wy] = this.iso(p.x + 0.5, p.y + 0.5);
    const W = this.c.clientWidth;
    const H = this.c.clientHeight;
    const h = this.relief === 2 ? 0 : this.bulk(this.relief === 1 ? lowPlot(p) : p)[1];
    const to = { x: W * 0.36 - wx * s, y: H * 0.52 - (wy - h / 2) * s, s };
    this.glide = { from: { ...this.cam }, to, t0: performance.now() };
    this.pulse = performance.now();
  }

  /** 这块地的地面中心在画面上的哪(无头浏览器试玩时按它点地图)。 */
  screenOf(id: number): [number, number] | null {
    const p = this.city?.plots.find((q) => q.id === id);
    if (!p) return null;
    return this.toScreen(...this.iso(p.x + 0.5, p.y + 0.5));
  }

  // ---------- 坐标 ----------

  private iso(x: number, y: number): [number, number] {
    return [(x - y) * (TW / 2), (x + y) * (TH / 2)];
  }

  private toScreen(wx: number, wy: number): [number, number] {
    return [wx * this.cam.s + this.cam.x, wy * this.cam.s + this.cam.y];
  }

  /**
   * 点和停都按地面上的那一格算,三档一样:鼠标底下是哪一格地,就是哪块地。不按楼身找——
   * 高楼的楼身盖着后面几排地,按楼找的话高楼后面的矮楼、空地永远点不到;挡在前面的楼画成半透明(见 ghosts)。
   */
  private plotAt(sx: number, sy: number): Plot | null {
    if (!this.city) return null;
    const wx = (sx - this.cam.x) / this.cam.s;
    const wy = (sy - this.cam.y) / this.cam.s;
    const x = Math.floor((wx / (TW / 2) + wy / (TH / 2)) / 2);
    const y = Math.floor((wy / (TH / 2) - wx / (TW / 2)) / 2);
    const ix = this.index(this.city);
    const k = this.cellAt(ix, x, y);
    return k < 0 ? null : ix.ord[k];
  }

  /**
   * 一座城的地块按画的顺序(从后往前)排好、按格子编好号:同一座城只算一次(原来每画一帧排一遍序)。
   * low 是矮楼那一档画的样子(用到才算),hmax 是每一档最高的楼画多高(找挡住一块地的楼时往前看几排)。
   */
  private ix: { city: City; ord: Plot[]; low: Plot[] | null; cell: Int32Array; at: Map<number, number>; hmax: number[]; geometry: number[]; order: number[]; w: number; h: number } | null = null;
  private index(city: City) {
    if (this.ix?.city === city) return this.ix;
    const old = this.ix;
    // 每日JSON是新对象,但格网通常不变。只复用真实相同的几何,楼高/进度仍换成本次快照。
    if (old && old.w === city.w && old.h === city.h && old.geometry.length === city.plots.length * 3 &&
        city.plots.every((p, i) => p.id === old.geometry[i * 3] && p.x === old.geometry[i * 3 + 1] && p.y === old.geometry[i * 3 + 2])) {
      old.city = city;
      old.ord = old.order.map(i => city.plots[i]);
      old.low = null;
      old.hmax = [-1, -1, -1];
      return old;
    }
    const order = city.plots.map((_, i) => i).sort((i, j) => {
      const a = city.plots[i], b = city.plots[j];
      return a.x + a.y - (b.x + b.y) || a.x - b.x;
    });
    const ord = order.map(i => city.plots[i]);
    const cell = new Int32Array(Math.max(0, city.w * city.h)).fill(-1);
    const at = new Map<number, number>();
    ord.forEach((p, k) => {
      if (p.x >= 0 && p.y >= 0 && p.x < city.w && p.y < city.h) cell[p.y * city.w + p.x] = k;
      at.set(p.id, k);
    });
    this.ix = { city, ord, low: null, cell, at, hmax: [-1, -1, -1], geometry: city.plots.flatMap(p => [p.id, p.x, p.y]), order, w: city.w, h: city.h };
    return this.ix;
  }

  /** 只裁绘制,不裁点击/遮挡/道路邻接索引。范围按未缩放的画布坐标比较。 */
  private viewport: [number, number, number, number] = [-Infinity, -Infinity, Infinity, Infinity];
  private inView(x: number, y: number, side = TW * 2, up = TH * 3, down = TH * 3): boolean {
    const [cx, cy] = this.iso(x + 0.5, y + 0.5);
    const [left, top, right, bottom] = this.viewport;
    // 留出描边/抗锯齿的屏幕像素,缩放时也不切掉边缘。
    const edge = 4 / this.cam.s;
    return cx + side + edge >= left && cx - side - edge <= right && cy + down + edge >= top && cy - up - edge <= bottom;
  }

  private visiblePlot(p: Plot): boolean {
    // 厂房层高最高;电视塔有最低塔高。另留屋顶、烟囱、塔吊和名牌的高度。
    const h = this.relief === 2 ? 0 : Math.max(p.floors, p.type === 'tvtower' ? 30 : p.kind === 'public' ? 6 : 0) * FH * 1.6;
    const label = p.publicName ?? p.label ?? '';
    // 最长阴影向+x延伸2.2格。名牌按全角字宽保守留边,不依赖上一块地的字体。
    const side = Math.max(TW * 2, label.length * 5 + 32);
    // 倾斜建筑绕底角旋转;把整个楼高同时留给左右和下方,不只按地面点裁高楼。
    const turn = this.relief !== 2 && (p.lean ?? 0) !== 0 ? h + 64 : 0;
    return this.inView(p.x, p.y, side + turn, h + 96, TH * 3 + turn);
  }

  // 仅留当前视口的一组列表。动画、灯光、车人仍逐帧照画,不缓存实际像素/绘制命令。
  private viewLists: { city: City; order: Plot[]; relief: number; scale: number; left: number; top: number; right: number; bottom: number; visible: Plot[]; ground: Plot[] } | null = null;
  private visibleLists(city: City, order: Plot[]) {
    const [left, top, right, bottom] = this.viewport;
    const old = this.viewLists;
    if (old && old.city === city && old.order === order && old.relief === this.relief && old.scale === this.cam.s &&
        old.left === left && old.top === top && old.right === right && old.bottom === bottom) {
      this.listStats.hits++;
      return old;
    }
    const visible = order.filter(p => this.visiblePlot(p));
    const visibleIds = new Set(visible.map(p => p.id));
    const ground = city.plots.filter(p => visibleIds.has(p.id));
    this.listStats.builds++;
    this.listStats.visibilityTests += order.length;
    this.listStats.groundTests += city.plots.length;
    return (this.viewLists = { city, order, relief: this.relief, scale: this.cam.s, left, top, right, bottom, visible, ground });
  }

  /** 第 (x, y) 格是排好序的第几块地(没有地是 -1)。 */
  private cellAt(ix: NonNullable<CityMap['ix']>, x: number, y: number): number {
    const { w, h } = ix.city;
    return x < 0 || y < 0 || x >= w || y >= h ? -1 : ix.cell[y * w + x];
  }

  /** 眼下这一档画的样子(立体、平面是原样,矮楼换成压低的)。 */
  private shown(ix: NonNullable<CityMap['ix']>): Plot[] {
    if (this.relief !== 1) return ix.ord;
    return (ix.low ??= ix.ord.map(lowPlot));
  }

  /**
   * 一栋楼在画面上占多大:底面往里缩几成(格)、连楼顶画多高(像素),和 building 画出来的对得上
   * (天线、旗杆、塔吊这些细的不算)。找哪几栋楼挡住一块地、镜头对准楼的半腰,都按它算;改了楼的画法要跟着改。
   */
  private bulk(p: Plot): [number, number] {
    if (p.kind === 'public') {
      const built = p.projectId && p.stage !== 'collapsed' && !p.closed;
      if (!built) return [0.3, 8];
      switch (p.type) {
        case 'school': return [0.14, 3 * FH + 8];
        case 'hospital': return [0.15, Math.max(4, p.floors) * FH];
        case 'police': return [0.18, 3 * FH + 4];
        case 'cityhall': return [0.18, Math.max(6, p.floors) * FH + 6];
        case 'tvtower': return [0.32, Math.max(this.relief === 1 ? lowFloors(30) : 30, p.floors) * FH * 0.82 + 9]; // 塔身细,球冠在八成多高
        case 'stadium': return [0.08, 14];
        case 'theater': return [0.12, 2 * FH + 30];
        case 'expo': return [0.06, 4 * FH + 20];
      }
    } else if (!p.projectId) return p.kind === 'hq' ? [0.34, 7] : [0.5, 0];
    if (!p.projectId || p.stage === 'collapsed' || p.floors === 0) return [0.5, 0];
    const stage = p.stage ?? '';
    const low = p.type !== 'works' && p.floors <= 3;
    const h = p.floors * FH * (p.type === 'works' ? 1.6 : low ? 1.4 : 1);
    const widths = p.widths ?? [];
    if (widths.some((w) => Math.abs(w - 1) > 0.01)) return [Math.max(0.02, Math.min(0.45, 0.5 - 0.3 * Math.min(1.5, Math.max(...widths)))), h];
    const done = (stage === 'delivered' || stage === 'acceptance') && !p.closed && !p.stalled;
    if (done && p.type === 'works') return [0.06, h];
    const tall = done && ((p.type === 'housing' && !low) || p.type === 'highrise' || p.type === 'office' || p.type === 'landmark');
    if (tall) return [p.type === 'office' ? 0.2 : p.type === 'highrise' ? 0.22 : 0.17, h + 8];
    return [p.type === 'shophouse' ? 0.25 : 0.2, h + (done ? 8 : 0)];
  }

  /**
   * 挡住鼠标停的、选中的那块地的楼(画在它后面、屏幕上盖住它那块地的),画成半透明,看得见那块地。
   * 楼在屏幕上的轮廓折回地面上,是它的底面往后(−x、−y 一起)拖出「楼高 ÷ 菱形高」格的六边形;
   * 六边形落在那块地里的面积就是屏幕上盖住它的那一块(对一个立方的楼是准的),盖住一成半以上才画淡。
   * 挡得着的只有它前面一窄条(x、y 都不比它小,两个差不超过一格),往前看几排按全城最高的楼定:
   * 一块地只看几十栋,不用每帧把全城的楼算一遍。
   */
  private ghosts(ix: NonNullable<CityMap['ix']>, list: Plot[]): Set<number> | null {
    if (this.hovered == null && this.selected == null) return null;
    const r = this.relief;
    if (ix.hmax[r] < 0) {
      let m = 0;
      for (const p of list) m = Math.max(m, this.bulk(p)[1]);
      ix.hmax[r] = m;
    }
    const reach = Math.ceil(ix.hmax[r] / TH) + 1;
    let out: Set<number> | null = null;
    for (const id of [this.hovered, this.selected]) {
      const k = id == null ? undefined : ix.at.get(id);
      if (k === undefined) continue;
      const t = list[k];
      for (let dx = 0; dx <= reach; dx++) {
        for (let dy = Math.max(0, dx - 1); dy <= Math.min(reach, dx + 1); dy++) {
          if (!dx && !dy) continue;
          const j = this.cellAt(ix, t.x + dx, t.y + dy);
          if (j < 0) continue;
          const [i, h] = this.bulk(list[j]);
          if (h <= 0) continue;
          // 那块地是 [0, 1]²,这栋的底面 [a0, a1] × [b0, b1],往后拖 H 格:先看拖的路上碰不碰得着,碰得着再算盖住多少
          const H = h / TH, a0 = dx + i, a1 = dx + 1 - i, b0 = dy + i, b1 = dy + 1 - i;
          if (Math.max(0, a0 - 1, b0 - 1) >= Math.min(H, a1, b1)) continue;
          const hex = [a0 - H, b0 - H, a1 - H, b0 - H, a1, b0, a1, b1, a0, b1, a0 - H, b1 - H];
          if (areaInCell(hex) >= GHOST_SHARE) (out ??= new Set()).add(list[j].id);
        }
      }
    }
    return out;
  }

  // ---------- 操作 ----------

  private setHover(p: Plot | null, x: number, y: number) {
    if ((p?.id ?? null) !== this.hovered) this.dirty = this.urgent = true;
    this.hovered = p?.id ?? null;
    this.c.style.cursor = p ? 'pointer' : '';
    this.onHover?.(p, x, y);
  }

  private down = (e: PointerEvent) => {
    this.c.setPointerCapture(e.pointerId);
    this.pointers.set(e.pointerId, { x: e.offsetX, y: e.offsetY });
    this.dragged = 0;
    if (this.pointers.size === 2) this.pinch = this.pinchDist();
    this.setHover(null, 0, 0);
  };

  private leave = () => {
    if (!this.pointers.size) this.setHover(null, 0, 0);
  };

  private move = (e: PointerEvent) => {
    const prev = this.pointers.get(e.pointerId);
    if (!prev) {
      // 没按着:鼠标停在哪块地上
      if (e.pointerType === 'mouse') this.setHover(this.plotAt(e.offsetX, e.offsetY), e.offsetX, e.offsetY);
      return;
    }
    if (this.dragged > 6) this.c.style.cursor = 'grabbing';
    const cur = { x: e.offsetX, y: e.offsetY };
    if (this.pointers.size === 1) {
      this.dirty = this.urgent = true;
      this.cam.x += cur.x - prev.x;
      this.cam.y += cur.y - prev.y;
      this.dragged += Math.abs(cur.x - prev.x) + Math.abs(cur.y - prev.y);
    }
    this.pointers.set(e.pointerId, cur);
    if (this.pointers.size === 2) {
      const d = this.pinchDist();
      if (this.pinch > 0) this.zoomAt(d / this.pinch, cur.x, cur.y);
      this.pinch = d;
      this.dragged += 10;
    }
  };

  private up = (e: PointerEvent) => {
    const wasTap = this.pointers.size === 1 && this.dragged < 6;
    this.pointers.delete(e.pointerId);
    if (this.pointers.size < 2) this.pinch = 0;
    if (!this.pointers.size) this.c.style.cursor = '';
    if (wasTap) {
      const p = this.plotAt(e.offsetX, e.offsetY);
      // 经 select 重画:暂停着的时候不重画,金框和挡着它的楼变淡要等鼠标再动一下才出来
      this.select(p?.id ?? null);
      this.onSelect(p);
    }
  };

  private wheel = (e: WheelEvent) => {
    e.preventDefault();
    this.zoomAt(Math.exp(-e.deltaY * 0.0015), e.offsetX, e.offsetY);
  };

  private pinchDist(): number {
    const [a, b] = [...this.pointers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  private zoomAt(f: number, sx: number, sy: number) {
    this.dirty = this.urgent = true;
    const s = Math.min(3, Math.max(0.35, this.cam.s * f));
    const k = s / this.cam.s;
    this.cam.x = sx - (sx - this.cam.x) * k;
    this.cam.y = sy - (sy - this.cam.y) * k;
    this.cam.s = s;
  }

  // ---------- 画 ----------

  private frame = (t: number) => {
    this.raf = requestAnimationFrame(this.frame);
    const dtc = Math.min(0.1, Math.max(0, (t - this.lastT) / 1000));
    this.lastT = t;
    if (this.moving) this.ct += dtc;
    // 同一RAF和原动画钟继续；隐藏时连clientWidth/Height布局读取也不需要。
    if (document.hidden) return;
    const [W, H] = fitCanvas(this.c, this.cap);
    if (!W || !H || !this.city) return;
    // 都停着、也没变化:不重画(画布大小变了会清空,得重画)
    if (W !== this.lastW || H !== this.lastH) {
      this.lastW = W;
      this.lastH = H;
      this.dirty = this.urgent = true;
    }
    const busy = this.moving || !!this.glide || t - this.pulse < 2100 || !this.fitted;
    if (!busy && !this.dirty) return;
    // 急的(拖、缩放、鼠标换了一块地、选中、镜头在飞)马上画;城市过了一天一秒最多画十次;只是车船在动一秒二十次。
    // 原来城市每过一天都当场重画,一秒三十天就一秒画六十张整座城,连八十个人的小村子都卡
    const gap = this.urgent || this.glide ? 0 : this.dirty ? 100 : 50;
    if (t - this.lastDraw < gap) return;
    const t0 = performance.now();
    this.lastDraw = t;
    this.dirty = false;
    this.urgent = false;
    const city = this.city;
    if (!this.fitted) {
      // 第一次:整座城装进画面,居中偏下(上面留给高楼)。
      // 第一次:城的一大半装进画面,对准自己的楼(没有就对准城中心)。
      const mapW = (city.w + city.h) * (TW / 2);
      this.cam.s = Math.max(0.55, Math.min(1.3, W / (mapW * 0.62)));
      const mine = city.plots.filter((p) => p.mine);
      const fx = mine.length ? (mine.reduce((a, p) => a + p.x, 0) / mine.length + city.w / 2) / 2 : city.w / 2;
      const fy = mine.length ? (mine.reduce((a, p) => a + p.y, 0) / mine.length + city.h / 2) / 2 : city.h / 2;
      const [cx, cy] = this.iso(fx + 0.5, fy + 0.5);
      this.cam.x = W / 2 - cx * this.cam.s;
      this.cam.y = H * 0.55 - cy * this.cam.s;
      this.fitted = true;
    }
    if (this.glide) {
      // 定位:半秒内缓缓飞过去
      const k = Math.min(1, (t - this.glide.t0) / 500);
      const e = 1 - (1 - k) ** 3;
      const { from, to } = this.glide;
      this.cam = { x: from.x + (to.x - from.x) * e, y: from.y + (to.y - from.y) * e, s: from.s + (to.s - from.s) * e };
      if (k >= 1) this.glide = null;
    }
    this.viewport = [-this.cam.x / this.cam.s, -this.cam.y / this.cam.s, (W - this.cam.x) / this.cam.s, (H - this.cam.y) / this.cam.s];
    const g = this.g;
    const sky = skyAt(city.hour);
    const measure = this.measureDraws;
    let phase = measure ? performance.now() : 0;
    let groundMs = 0, shadowMs = 0, buildingsMs = 0, dynamicMs = 0;
    drawSky(g, W, H, sky);
    g.save();
    g.translate(this.cam.x, this.cam.y);
    g.scale(this.cam.s, this.cam.s);
    this.outskirts(city, sky);
    // 地面(路、人行道、每块地)先画完,再画影子,楼按从后往前的顺序画,前面的楼挡住后面的。
    const ix = this.index(city);
    const order = this.shown(ix);
    const { visible, ground } = this.visibleLists(city, order);
    const lots = this.lotsOf(city);
    for (const p of ground) this.tile(p, sky, lots.get(p.id)!);
    this.streetMarks(ground, lots, sky);
    const ov = this.overlay;
    if (ov) {
      for (const p of ground) {
        const c = ov[p.id];
        if (!c) continue;
        this.quad(...this.inner(p, lots.get(p.id)!));
        g.fillStyle = c;
        g.fill();
      }
    }
    // 楼高这一档画的样子(矮楼换成压低了的);平面不立楼,也就没有影子
    const flat = this.relief === 2;
    if (measure) { const now = performance.now(); groundMs = now - phase; phase = now; }
    // 阴影仍按原全城路径一次fill:删掉屏外子路径会改变Canvas边缘栅格化的舍入。
    if (!flat) this.shadows(order, sky, !!ov);
    if (measure) shadowMs = performance.now() - phase;
    // 鼠标停的、选中的那一格:先在地面上铺一层亮色(挡在前面的楼画成半透明,透过去看得见),描边最后画在最上面
    const byId = (id: number | null) => (id == null ? undefined : ix.ord[ix.at.get(id) ?? -1]);
    const hov = this.hovered !== this.selected ? byId(this.hovered) : undefined;
    const sel = byId(this.selected);
    if (hov) {
      this.diamond(hov.x, hov.y);
      g.fillStyle = 'rgba(255,250,236,0.32)';
      g.fill();
    }
    if (sel) {
      this.diamond(sel.x, sel.y, 0.02);
      g.fillStyle = 'rgba(224,178,76,0.24)';
      g.fill();
    }
    const alpha = ov ? 0.42 : 1;
    g.globalAlpha = alpha;
    if (city.lines?.length && this.transit === 0) this.subway(city);
    const fade = flat ? null : this.ghosts(ix, order);
    for (const p of visible) {
      const lot = lots.get(p.id)!;
      if (measure) phase = performance.now();
      this.traffic(p, lot, sky, true);
      this.walkers(p, lot, sky, true);
      if (measure) { const now = performance.now(); dynamicMs += now - phase; phase = now; }
      this.trees(p, lot, sky, true);
      this.lotProps(p, lot, sky);
      if (fade?.has(p.id)) g.globalAlpha = alpha * GHOST;
      if (flat) this.footprint(p, sky, t);
      else this.building(p, sky, t);
      g.globalAlpha = alpha;
      this.trees(p, lot, sky, false);
      if (measure) { const now = performance.now(); buildingsMs += now - phase; phase = now; }
      this.walkers(p, lot, sky, false);
      this.traffic(p, lot, sky, false);
      if (measure) dynamicMs += performance.now() - phase;
      if (p.guild) this.guildFlag(p);
    }
    g.globalAlpha = 1;
    if (hov) {
      this.diamond(hov.x, hov.y, 0.03);
      g.strokeStyle = 'rgba(255,250,236,0.85)';
      g.lineWidth = 1.5 / this.cam.s;
      g.stroke();
    }
    if (sel) this.outline(sel, t, false);
    g.restore();
    this.drawMarks(city);
    if (city.weather === 'rain' || city.weather === 'storm' || city.weather === 'typhoon') {
      drawRain(g, W, H, t, city.weather !== 'rain');
    }
    if (sky.night > 0) {
      g.fillStyle = `rgba(10,16,40,${sky.night * 0.28})`;
      g.fillRect(0, 0, W, H);
    }
    if (this.transit === 1) this.transitMap(city, sky, W, H, t);
    drawGrain(g, W, H);
    const elapsed = performance.now() - t0;
    this.drawMs += elapsed;
    this.draws++;
    if (measure) {
      this.drawStages.frames++;
      this.drawStages.groundMs += groundMs;
      this.drawStages.shadowMs += shadowMs;
      this.drawStages.buildingsMs += buildingsMs;
      this.drawStages.dynamicMs += dynamicMs;
      this.drawStages.otherMs += Math.max(0, elapsed - groundMs - shadowMs - buildingsMs - dynamicMs);
    }
  };

  /** 城外:田、土路和江一直铺到天边,越远越淡,城不是悬在天上的一块板。 */
  private outskirts(city: City, sky: Sky) {
    const g = this.g;
    const night = sky.night;
    const R = 8;
    // 江在城的北边(y < 0 那一侧)流过,江景那一排地就在江边;江对岸还是田。
    for (let y = -R; y < city.h + R; y++) {
      for (let x = -R; x < city.w + R; x++) {
        if (x >= 0 && y >= 0 && x < city.w && y < city.h) continue;
        if (!this.inView(x, y)) continue;
        const isWater = y < 0 && y >= -4;
        const v = hash(x * 31 + y * 17);
        this.diamond(x, y);
        g.fillStyle = isWater ? water[hash(x * 7 + y * 13) < 0.5 ? 0 : 1] : v < 0.12 ? ground.residential[1] : FIELDS[Math.floor(hash(x * 13 + y * 29) * FIELDS.length)];
        g.fill();
        if (isWater) {
          // 波光:几道短短的亮线顺着江慢慢漂
          const k = hash(x * 5 + y * 3);
          const ph = (this.ct * 0.06 + k) % 1;
          const [cx, cy] = this.iso(x + ph, y + 0.3 + k * 0.4);
          g.strokeStyle = `rgba(255,255,255,${(0.34 * Math.sin(ph * Math.PI)).toFixed(3)})`;
          g.lineWidth = 1;
          g.beginPath();
          g.moveTo(cx - 6, cy);
          g.quadraticCurveTo(cx, cy - 2, cx + 6, cy);
          g.stroke();
        } else if (v > 0.93) {
          const [cx, cy] = this.iso(x + 0.5, y + 0.5);
          g.fillStyle = night > 0.5 ? '#3f5a3c' : '#6f9a5e';
          g.beginPath();
          g.arc(cx, cy - 5, 5, 0, Math.PI * 2);
          g.fill();
        }
        // 越远越淡:盖一层地平线的雾(树也在雾里)
        const d = Math.max(-x - 1, -y - 1, x - city.w, y - city.h);
        this.diamond(x, y);
        g.fillStyle = night > 0.5 ? `rgba(20,28,50,${Math.min(0.85, 0.1 + d * 0.07)})` : `rgba(226,232,232,${Math.min(0.85, 0.12 + d * 0.07)})`;
        g.fill();
      }
    }
    // 江上的船:货船、渡船来来去去(暂停时停着)
    const span = city.w + 2 * R;
    for (let i = 0; i < 4; i++) {
      const dir = i % 2 ? -1 : 1;
      const u = (this.ct * (0.05 + hash(i * 7) * 0.04) + hash(i * 13)) % 1;
      const bx = -R + (dir > 0 ? u : 1 - u) * span;
      const by = -3.6 + i * 0.75;
      const len = 0.3 + hash(i * 3) * 0.25;
      const x0 = dir > 0 ? bx - len : bx;
      // 船尾的水花
      g.strokeStyle = 'rgba(255,255,255,0.35)';
      g.lineWidth = 1;
      g.beginPath();
      this.seg(dir > 0 ? x0 - 0.35 : x0 + len, by + 0.06, dir > 0 ? x0 : x0 + len + 0.35, by + 0.06);
      g.stroke();
      this.solid(x0, by, x0 + len, by + 0.12, 2.2, i % 3 ? '#6b4f33' : '#7a4a3a');
      g.save();
      g.translate(0, -2.2);
      const cab = dir > 0 ? x0 : x0 + len * 0.6;
      this.solid(cab + 0.03, by + 0.02, cab + len * 0.4, by + 0.1, 3, '#e8e2d6', '#d9d4cb');
      if (night > 0.5) {
        const [lx, ly] = this.iso(cab + len * 0.2, by + 0.1);
        g.fillStyle = '#ffd98f';
        g.fillRect(lx - 1, ly - 2, 2, 1.2);
      }
      g.restore();
    }
    // 城外一圈土路
    g.strokeStyle = 'rgba(150,120,80,0.55)';
    g.lineWidth = 3;
    const [ax, ay] = this.iso(-0.5, -0.5);
    const [bx, by] = this.iso(city.w + 0.5, -0.5);
    const [cx, cy] = this.iso(city.w + 0.5, city.h + 0.5);
    const [dx, dy] = this.iso(-0.5, city.h + 0.5);
    g.beginPath();
    g.moveTo(ax, ay);
    g.lineTo(bx, by);
    g.lineTo(cx, cy);
    g.lineTo(dx, dy);
    g.closePath();
    g.stroke();
    g.lineWidth = 1;
  }

  private diamond(x: number, y: number, inset = 0) {
    const g = this.g;
    const [ax, ay] = this.iso(x + inset, y + inset);
    const [bx, by] = this.iso(x + 1 - inset, y + inset);
    const [cx, cy] = this.iso(x + 1 - inset, y + 1 - inset);
    const [dx, dy] = this.iso(x + inset, y + 1 - inset);
    g.beginPath();
    g.moveTo(ax, ay);
    g.lineTo(bx, by);
    g.lineTo(cx, cy);
    g.lineTo(dx, dy);
    g.closePath();
  }

  /** 世界坐标里的一块长方形 [xa, xb] × [ya, yb](画出来是菱形)。fresh 为假时接着当前的路径画(几块合成一块再上色)。 */
  private quad(xa: number, ya: number, xb: number, yb: number, fresh = true, path?: Path2D) {
    const g = path ?? this.g;
    const [ax, ay] = this.iso(xa, ya);
    const [bx, by] = this.iso(xb, ya);
    const [cx, cy] = this.iso(xb, yb);
    const [dx, dy] = this.iso(xa, yb);
    if (fresh) this.g.beginPath();
    g.moveTo(ax, ay);
    g.lineTo(bx, by);
    g.lineTo(cx, cy);
    g.lineTo(dx, dy);
    g.closePath();
  }

  /** 世界坐标里一块长方形上立起来的小盒子(车、集装箱、水箱):左面亮、右面暗、顶面(光从左上来)。 */
  private solid(xa: number, ya: number, xb: number, yb: number, h: number, wall: string, top?: string) {
    const g = this.g;
    const [ax, ay] = this.iso(xa, yb); // 左
    const [bx, by] = this.iso(xb, yb); // 前
    const [cx, cy] = this.iso(xb, ya); // 右
    const [dx, dy] = this.iso(xa, ya); // 后
    g.beginPath();
    g.moveTo(ax, ay);
    g.lineTo(bx, by);
    g.lineTo(bx, by - h);
    g.lineTo(ax, ay - h);
    g.closePath();
    g.fillStyle = wall;
    g.fill();
    g.beginPath();
    g.moveTo(bx, by);
    g.lineTo(cx, cy);
    g.lineTo(cx, cy - h);
    g.lineTo(bx, by - h);
    g.closePath();
    g.fillStyle = wall;
    g.fill();
    g.fillStyle = 'rgba(0,0,0,0.24)';
    g.fill();
    g.beginPath();
    g.moveTo(ax, ay - h);
    g.lineTo(bx, by - h);
    g.lineTo(cx, cy - h);
    g.lineTo(dx, dy - h);
    g.closePath();
    g.fillStyle = top ?? wall;
    g.fill();
    g.fillStyle = 'rgba(255,255,255,0.14)';
    g.fill();
  }

  /** 一辆车:车身、车厢(玻璃)、车顶;alongY 为真车头朝 ±y。夜里车头灯亮。 */
  private car(wx: number, wy: number, alongY: boolean, color: string, night: number, dir = 1) {
    const g = this.g;
    const l = 0.13, w = 0.06;
    const [xa, ya, xb, yb] = alongY ? [wx - w / 2, wy - l / 2, wx + w / 2, wy + l / 2] : [wx - l / 2, wy - w / 2, wx + l / 2, wy + w / 2];
    this.solid(xa, ya, xb, yb, 1.6, color);
    g.save();
    g.translate(0, -1.6);
    const c = l * 0.26;
    if (alongY) this.solid(xa + 0.006, ya + c, xb - 0.006, yb - c, 1.2, '#46525c', color);
    else this.solid(xa + c, ya + 0.006, xb - c, yb - 0.006, 1.2, '#46525c', color);
    g.restore();
    if (night > 0.4) {
      const [hx, hy] = this.iso(alongY ? wx : wx + (dir * l) / 2, alongY ? wy + (dir * l) / 2 : wy);
      g.save();
      g.globalCompositeOperation = 'lighter';
      g.drawImage(lamp(), hx - 5, hy - 3.5, 10, 6);
      g.restore();
      g.fillStyle = '#fff1c8';
      g.fillRect(hx - 0.7, hy - 1.4, 1.4, 1);
    }
  }

  /**
   * 街上的车:这块地靠前两边(back 为假)或城边上靠后两边(back 为真)的街。每条街两股车道对开,
   * 车按固定的间距顺着整条街往前挪(跨过路口接着走,不会一段一段地闪);哪一格有车按片区的繁忙程度抽,市中心多、郊区少。
   */
  private traffic(p: Plot, lot: Lot, sky: Sky, back: boolean) {
    // 车的多少:模拟里这块地一天过多少辆车(一格一天过一百二十辆开始堵);没有数据的按片区估
    const real = this.cars ? (this.cars[p.id] ?? 0) : null;
    const one = (L: number, S: number, hw: number, alongY: boolean) => {
      const dens = real != null ? Math.min(0.95, real / 150) : (TRAFFIC[p.district] ?? 0.3) * (hw === AV ? 1.3 : 1) * 0.5;
      if (dens <= 0) return;
      const v = hw === AV ? 0.3 : 0.2;
      for (const lane of [-1, 1]) {
        const ph = hash(Math.round(L * 977) + lane * 31 + (alongY ? 7 : 3));
        for (let j = 0; j < 2; j++) {
          const u = this.ct * v * lane + ph + j / 2;
          const t = u - Math.floor(u);
          const id = S - Math.floor(u);
          const k = hash(id * 7919 + j * 131 + lane * 17 + Math.round(L * 101) + (alongY ? 0 : 50021));
          if (k >= dens) continue;
          const pos = S + t;
          const off = L + lane * hw * 0.5;
          const color = CARS[Math.floor(hash(id * 37 + j + lane * 5 + Math.round(L * 3)) * CARS.length)];
          if (alongY) this.car(off, pos, true, color, sky.night, lane);
          else this.car(pos, off, false, color, sky.night, lane);
        }
      }
    };
    if (back) {
      if (lot.x0 && p.x === 0) one(p.x, p.y, lot.x0, true);
      if (lot.y0 && p.y === 0) one(p.y, p.x, lot.y0, false);
    } else {
      if (lot.x1) one(p.x + 1, p.y, lot.x1, true);
      if (lot.y1) one(p.y + 1, p.x, lot.y1, false);
    }
  }

  /**
   * 人行道上的行人:这块地四边临街的人行道,靠后两边(back)在楼之前画、靠前两边在楼之后画。
   * 人的多少按模拟里这块地的人流(住的、上班的、路过的),没有数据的按片区估;夜里少一大半。
   */
  private walkers(p: Plot, lot: Lot, sky: Sky, back: boolean) {
    if (!lot.paved) return;
    const foot = this.people ? (this.people[p.id] ?? 0) : (TRAFFIC[p.district] ?? 0.2) * 150;
    const dens = Math.min(0.85, foot / 300) * (1 - 0.6 * Math.min(1, sky.night));
    if (dens <= 0.01) return;
    const g = this.g;
    const side = (wL: number, S: number, alongY: boolean, k: number) => {
      for (let j = 0; j < 3; j++) {
        const seed = p.id * 97 + k * 13 + j;
        if (hash(seed) >= dens) continue;
        const dir = hash(seed * 3) < 0.5 ? -1 : 1;
        const t = (((this.ct * 0.05 * dir + hash(seed * 7)) % 1) + 1) % 1;
        const pos = S + 0.08 + t * 0.84;
        const [sx, sy] = alongY ? this.iso(wL, pos) : this.iso(pos, wL);
        const bob = this.moving ? Math.abs(Math.sin(this.ct * 7 + seed)) * 0.4 : 0;
        g.fillStyle = '#3a3530';
        g.fillRect(sx - 0.5, sy - 1.6, 1, 1.6);
        g.fillStyle = CARS[Math.floor(hash(seed * 5) * CARS.length)];
        g.fillRect(sx - 0.7, sy - 3.4 - bob, 1.4, 1.9);
        g.fillStyle = '#e2c4a4';
        g.beginPath();
        g.arc(sx, sy - 4.1 - bob, 0.75, 0, Math.PI * 2);
        g.fill();
      }
    };
    const mid = CURB / 2;
    if (back) {
      if (lot.x0) side(p.x + lot.x0 + mid, p.y, true, 0);
      if (lot.y0) side(p.y + lot.y0 + mid, p.x, false, 1);
    } else {
      if (lot.x1) side(p.x + 1 - lot.x1 - mid, p.y, true, 2);
      if (lot.y1) side(p.y + 1 - lot.y1 - mid, p.x, false, 3);
    }
  }

  /** 城区空着的地上的东西:停车场停的车、小广场的树(在楼那一轮画,前后挡得对)。 */
  private lotProps(p: Plot, lot: Lot, sky: Sky) {
    if (!this.emptyLot(p) || !lot.paved) return;
    const use = this.lotUse(p);
    if (!use) return;
    const [x0, y0, x1, y1] = this.inner(p, lot);
    if (use === 'parking') {
      // 两排车位,一排六个,停了几辆按地块号定(同一块地每次画都一样)
      for (const row of [0.2, 0.8]) {
        for (let k = 0; k < 6; k++) {
          const r = hash(p.id * 61 + k * 7 + row * 10);
          if (r > 0.6) continue;
          this.car(x0 + (x1 - x0) * (k + 0.5) / 6, y0 + (y1 - y0) * row, true, CARS[Math.floor(hash(p.id * 13 + k + row * 3) * CARS.length)], 0);
        }
      }
    } else if (use === 'plaza') {
      for (const [a, b] of [[0.15, 0.15], [0.85, 0.15], [0.15, 0.85], [0.85, 0.85]]) this.tree(x0 + (x1 - x0) * a, y0 + (y1 - y0) * b, sky, p.id * 3 + a * 10 + b * 5);
    }
  }

  /** 没盖楼、不是机构的地。 */
  private emptyLot(p: Plot): boolean {
    return !p.projectId && p.kind !== 'public' && p.kind !== 'hq' && p.kind !== 'dev' && !p.open;
  }

  /** 城区空地拿来干什么:市中心、市区是停车场或小广场。 */
  private lotUse(p: Plot): 'parking' | 'plaza' | null {
    if (p.district !== 'core' && p.district !== 'commercial') return null;
    return hash(p.id * 19 + 5) < 0.6 ? 'parking' : 'plaza';
  }

  /** 世界坐标里的一段线(接着当前路径)。 */
  private seg(xa: number, ya: number, xb: number, yb: number) {
    const [ax, ay] = this.iso(xa, ya);
    const [bx, by] = this.iso(xb, yb);
    this.g.moveTo(ax, ay);
    this.g.lineTo(bx, by);
  }

  /** 这块地去掉路和人行道以后自己的那一块。 */
  private inner(p: Plot, lot: Lot): [number, number, number, number] {
    const curb = lot.paved ? CURB : 0;
    const gap = lot.paved ? LOT : 0;
    return [
      p.x + (lot.x0 ? lot.x0 + curb : gap), p.y + (lot.y0 ? lot.y0 + curb : gap),
      p.x + 1 - (lot.x1 ? lot.x1 + curb : gap), p.y + 1 - (lot.y1 ? lot.y1 + curb : gap),
    ];
  }

  /** 每块地四边有没有路:同一座城只算一次。 */
  private lotCity: City | null = null;
  private lotMap = new Map<number, Lot>();
  private lotGeometry: number[] | null = null;
  private lotPaved: boolean[] = [];
  private lotsOf(city: City): Map<number, Lot> {
    if (this.lotCity === city) return this.lotMap;
    const ix = this.index(city);
    const paved = (p?: Plot) => !!p && (URBAN.has(p.district) || !!p.projectId || p.kind === 'public' || p.kind === 'hq');
    const flags = city.plots.map(paved);
    if (this.lotGeometry === ix.geometry && flags.length === this.lotPaved.length && flags.every((v, i) => v === this.lotPaved[i])) {
      this.lotCity = city;
      return this.lotMap;
    }
    const at = (x: number, y: number) => { const k = this.cellAt(ix, x, y); return k < 0 ? undefined : ix.ord[k]; };
    const m = new Map<number, Lot>();
    for (const p of city.plots) {
      const me = paved(p);
      // 边界 k 在街网上、两边至少一块有人用,就有一条路;每六格一条大街
      const side = (k: number, every: number, other?: Plot) => (k % every === 0 && (me || paved(other)) ? (k % AVENUE === 0 ? AV : ST) : 0);
      m.set(p.id, {
        x0: side(p.x, STREET_X, at(p.x - 1, p.y)),
        x1: side(p.x + 1, STREET_X, at(p.x + 1, p.y)),
        y0: side(p.y, STREET_Y, at(p.x, p.y - 1)),
        y1: side(p.y + 1, STREET_Y, at(p.x, p.y + 1)),
        paved: me,
      });
    }
    this.lotCity = city;
    this.lotGeometry = ix.geometry;
    this.lotPaved = flags;
    this.lotMap = m;
    return m;
  }

  /** 地面:临街的一边是半条路,城区的地沿路一道人行道,里面才是这块地自己的地面(铺装、草地、田)。 */
  private tile(p: Plot, sky: Sky, lot: Lot) {
    const g = this.g;
    const pal = ground[p.district] ?? ground.suburb;
    const r = hash(p.id * 7 + 3);
    const empty = !p.projectId && p.kind !== 'public';
    const farm = !lot.paved && p.district === 'suburb';
    const base = farm ? FIELDS[Math.floor(r * FIELDS.length)] : r < 0.5 ? pal[0] : pal[1];
    const street = lot.x0 || lot.x1 || lot.y0 || lot.y1;
    this.diamond(p.x, p.y);
    g.fillStyle = street ? ASPHALT : lot.paved ? SIDEWALK : base;
    g.fill();
    if (street && lot.paved) {
      this.quad(p.x + lot.x0, p.y + lot.y0, p.x + 1 - lot.x1, p.y + 1 - lot.y1);
      g.fillStyle = SIDEWALK;
      g.fill();
    }
    const [x0, y0, x1, y1] = this.inner(p, lot);
    this.quad(x0, y0, x1, y1);
    g.fillStyle = base;
    g.fill();
    const [cx, cy] = this.iso(p.x + 0.5, p.y + 0.5);
    if (farm && empty) {
      // 田垄:每块田的方向不一样
      const along = hash(p.id * 13 + 1) < 0.5;
      g.strokeStyle = 'rgba(120,95,50,0.28)';
      g.lineWidth = 1;
      g.beginPath();
      for (let i = 1; i < 6; i++) {
        const t = i / 6;
        if (along) this.seg(x0 + 0.06, y0 + (y1 - y0) * t, x1 - 0.06, y0 + (y1 - y0) * t);
        else this.seg(x0 + (x1 - x0) * t, y0 + 0.06, x0 + (x1 - x0) * t, y1 - 0.06);
      }
      g.stroke();
      return;
    }
    const use = lot.paved && this.emptyLot(p) ? this.lotUse(p) : null;
    if (use === 'parking') {
      // 停车场:沥青地面、两排白线车位,中间是车道(车在楼那一轮画)
      this.quad(x0, y0, x1, y1);
      g.fillStyle = '#9a948a';
      g.fill();
      g.strokeStyle = 'rgba(255,255,255,0.55)';
      g.lineWidth = 0.6;
      g.beginPath();
      for (let k = 0; k <= 6; k++) {
        const x = x0 + ((x1 - x0) * k) / 6;
        this.seg(x, y0 + 0.03, x, y0 + (y1 - y0) * 0.36);
        this.seg(x, y1 - (y1 - y0) * 0.36, x, y1 - 0.03);
      }
      g.stroke();
      return;
    }
    if (use === 'plaza') {
      // 小广场:铺装、中间一个喷水池、四角的树(树在楼那一轮画)
      g.strokeStyle = 'rgba(255,248,232,0.3)';
      g.lineWidth = 1;
      g.beginPath();
      for (const t of [0.25, 0.5, 0.75]) {
        this.seg(x0 + (x1 - x0) * t, y0, x0 + (x1 - x0) * t, y1);
        this.seg(x0, y0 + (y1 - y0) * t, x1, y0 + (y1 - y0) * t);
      }
      g.stroke();
      g.fillStyle = '#bdb3a2';
      g.beginPath();
      g.ellipse(cx, cy, TW * 0.17, TH * 0.17, 0, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = '#8fbcd6';
      g.beginPath();
      g.ellipse(cx, cy, TW * 0.13, TH * 0.13, 0, 0, Math.PI * 2);
      g.fill();
      // 水柱
      const up = 5 + Math.sin(this.ct * 3 + p.id) * 1.2;
      g.strokeStyle = 'rgba(235,245,250,0.85)';
      g.lineWidth = 1.2;
      g.beginPath();
      g.moveTo(cx, cy);
      g.lineTo(cx, cy - up);
      g.stroke();
      g.fillStyle = 'rgba(235,245,250,0.5)';
      g.beginPath();
      g.ellipse(cx, cy - 0.5, 3.5, 1.6, 0, 0, Math.PI * 2);
      g.fill();
      return;
    }
    if (p.district === 'river') {
      // 靠江一侧一道栏杆
      g.strokeStyle = 'rgba(90,90,90,0.45)';
      g.beginPath();
      this.seg(p.x, p.y + 0.06, p.x + 1, p.y + 0.06);
      g.stroke();
    }
    if (empty && !lot.paved && p.district === 'wild') {
      // 荒地:几丛野草、一两块石头
      g.strokeStyle = 'rgba(120,130,80,0.55)';
      g.lineWidth = 1;
      g.beginPath();
      for (let i = 0; i < 5; i++) {
        const [sx, sy] = this.iso(x0 + 0.1 + hash(p.id * 11 + i) * 0.8, y0 + 0.1 + hash(p.id * 23 + i) * 0.8);
        g.moveTo(sx - 1.5, sy);
        g.lineTo(sx - 0.5, sy - 2.5);
        g.moveTo(sx + 1.5, sy);
        g.lineTo(sx + 0.5, sy - 2.5);
      }
      g.stroke();
      if (hash(p.id * 5) < 0.5) {
        const [sx, sy] = this.iso(x0 + 0.3 + hash(p.id * 9) * 0.4, y0 + 0.5);
        g.fillStyle = '#a8a092';
        g.beginPath();
        g.ellipse(sx, sy - 1, 3, 1.8, 0, 0, Math.PI * 2);
        g.fill();
      }
    }
    if (empty && p.district === 'residential') {
      // 小公园:一条小路穿过去
      g.strokeStyle = 'rgba(232,222,200,0.8)';
      g.lineWidth = 2;
      g.beginPath();
      if (hash(p.id * 29) < 0.5) this.seg(x0, (y0 + y1) / 2, x1, (y0 + y1) / 2);
      else this.seg((x0 + x1) / 2, y0, (x0 + x1) / 2, y1);
      g.stroke();
      // 空着的居民区地块是一小片绿地,几棵树
      for (let i = 0; i < 3; i++) {
        const ox = (hash(p.id * 3 + i) - 0.5) * TW * 0.4;
        const oy = (hash(p.id * 5 + i) - 0.5) * TH * 0.36;
        g.fillStyle = sky.night > 0.5 ? '#3f5a3c' : '#6f9a5e';
        g.beginPath();
        g.arc(cx + ox, cy + oy - 5, 4.5, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = '#6b4f33';
        g.fillRect(cx + ox - 0.8, cy + oy - 1, 1.6, 3);
      }
    } else if (p.district === 'core') {
      // 市中心:石材铺装的砖缝
      g.strokeStyle = 'rgba(60,45,30,0.14)';
      g.lineWidth = 1;
      g.beginPath();
      for (const t of [1 / 3, 2 / 3]) {
        this.seg(x0 + (x1 - x0) * t, y0, x0 + (x1 - x0) * t, y1);
        this.seg(x0, y0 + (y1 - y0) * t, x1, y0 + (y1 - y0) * t);
      }
      g.stroke();
    }
  }

  /** 大街画中线;天黑以后路灯一盏盏亮起来,照亮路面(楼画在后面,会挡住楼后面的灯)。 */
  private streetMarks(plots: Plot[], lots: Map<number, Lot>, sky: Sky) {
    const g = this.g;
    g.save();
    g.strokeStyle = 'rgba(246,238,214,0.5)';
    g.lineWidth = 0.8;
    g.setLineDash([3, 3]);
    g.beginPath();
    for (const p of plots) {
      const l = lots.get(p.id)!;
      if (l.x0 === AV && p.x > 0) this.seg(p.x, p.y + 0.14, p.x, p.y + 0.86);
      if (l.y0 === AV && p.y > 0) this.seg(p.x + 0.14, p.y, p.x + 0.86, p.y);
    }
    g.stroke();
    g.setLineDash([]);
    if (sky.night > 0.3) {
      const img = lamp();
      g.globalCompositeOperation = 'lighter';
      g.globalAlpha = Math.min(1, (sky.night - 0.3) / 0.5);
      const light = (wx: number, wy: number) => {
        const [sx, sy] = this.iso(wx, wy);
        g.drawImage(img, sx - 13, sy - 7, 26, 14);
        g.fillStyle = 'rgba(255,236,190,0.9)';
        g.fillRect(sx - 0.8, sy - 5.5, 1.6, 1.6);
      };
      for (const p of plots) {
        const l = lots.get(p.id)!;
        if (!l.paved) continue;
        if (l.x1) light(p.x + 1 - l.x1 - 0.012, p.y + 0.5);
        if (l.y1) light(p.x + 0.5, p.y + 1 - l.y1 - 0.012);
      }
    }
    g.restore();
  }

  /**
   * 影子:光从左上来,影子朝右下(世界坐标的 +x 方向)铺在地上,楼越高越长;清晨傍晚拉长、变淡,夜里没有。
   * 所有楼的影子拼成一条路径一次上色,叠在一起的地方不会更黑。faint:地图模式开着,影子淡一半。
   */
  // 只留最近的完整路径,不累计各城市/日/镜头。bounds按原子路径顺序保存实际四角参数。
  private shadowPath: { path: Path2D; bounds: number[] } | null = null;
  private shadows(plots: Plot[], sky: Sky, faint: boolean) {
    if (sky.night > 0.85) return;
    const g = this.g;
    const long = 0.014 + 0.026 * Math.min(1, sky.night / 0.45);
    const alpha = 0.2 * (1 - sky.night) * (faint ? 0.5 : 1);
    g.beginPath();
    if (typeof Path2D !== 'undefined') {
      let same = this.shadowPath !== null, k = 0;
      const bounds = this.shadowPath?.bounds;
      if (bounds) for (const p of plots) {
        if (p.stage === 'collapsed' || (p.floors <= 0 && p.kind !== 'public')) continue;
        const h = p.kind === 'public' ? Math.max(3, p.floors) * FH : p.floors * FH;
        const i = p.kind === 'public' ? 0.14 : 0.18;
        if (bounds[k] !== p.x + i || bounds[k + 1] !== p.y + i || bounds[k + 2] !== p.x + 1 - i + Math.min(2.2, h * long) || bounds[k + 3] !== p.y + 1 - i) { same = false; break; }
        k += 4;
      }
      if (same && k === bounds!.length) this.shadowStats.hits++;
      else {
        const path = new Path2D(), bounds: number[] = [];
        for (const p of plots) {
          if (p.stage === 'collapsed' || (p.floors <= 0 && p.kind !== 'public')) continue;
          const h = p.kind === 'public' ? Math.max(3, p.floors) * FH : p.floors * FH;
          const i = p.kind === 'public' ? 0.14 : 0.18;
          const xa = p.x + i, ya = p.y + i, xb = p.x + 1 - i + Math.min(2.2, h * long), yb = p.y + 1 - i;
          bounds.push(xa, ya, xb, yb);
          this.quad(xa, ya, xb, yb, false, path);
        }
        this.shadowPath = { path, bounds };
        this.shadowStats.builds++;
      }
      if (this.shadowPath!.bounds.length) {
        g.fillStyle = `rgba(48,36,24,${alpha.toFixed(3)})`;
        g.fill(this.shadowPath!.path);
      }
      return;
    }
    // 没有Path2D的环境保留原即时路径,不降画质。
    let any = false;
    for (const p of plots) {
      if (p.stage === 'collapsed' || (p.floors <= 0 && p.kind !== 'public')) continue;
      const h = p.kind === 'public' ? Math.max(3, p.floors) * FH : p.floors * FH;
      const i = p.kind === 'public' ? 0.14 : 0.18;
      this.quad(p.x + i, p.y + i, p.x + 1 - i + Math.min(2.2, h * long), p.y + 1 - i, false);
      any = true;
    }
    if (!any) return;
    g.fillStyle = `rgba(48,36,24,${alpha.toFixed(3)})`;
    g.fill();
  }

  /**
   * 行道树:居民区、江边的街边种树,市区、市中心只在大街边上种。
   * back 为真种这块地靠后的两边(在楼之前画),否则种靠前的两边(在楼之后画,挡在楼前)。
   */
  private trees(p: Plot, lot: Lot, sky: Sky, back: boolean) {
    if (!lot.paved) return;
    const avenueOnly = p.district === 'commercial' || p.district === 'core';
    if (!avenueOnly && p.district !== 'residential' && p.district !== 'river') return;
    const ok = (w: number) => w > 0 && (!avenueOnly || w === AV);
    const d = 0.016;
    const plant = (wx: number, wy: number, k: number) => this.tree(wx, wy, sky, p.id * 17 + k);
    if (back) {
      if (ok(lot.x0)) [0.28, 0.72].forEach((t, k) => plant(p.x + lot.x0 + d, p.y + t, k));
      if (ok(lot.y0)) [0.28, 0.72].forEach((t, k) => plant(p.x + t, p.y + lot.y0 + d, k + 2));
    } else {
      if (ok(lot.x1)) [0.28, 0.72].forEach((t, k) => plant(p.x + 1 - lot.x1 - d, p.y + t, k + 4));
      if (ok(lot.y1)) [0.28, 0.72].forEach((t, k) => plant(p.x + t, p.y + 1 - lot.y1 - d, k + 6));
    }
  }

  /** 一棵树:树干、树冠,左上一点亮(光从左上来)。 */
  private tree(wx: number, wy: number, sky: Sky, seed: number) {
    const g = this.g;
    const [sx, sy] = this.iso(wx, wy);
    const r = 2.8 + hash(seed) * 1.3;
    const dark = sky.night > 0.5;
    g.fillStyle = '#6b4f33';
    g.fillRect(sx - 0.6, sy - 3, 1.2, 3);
    g.fillStyle = dark ? '#34503a' : hash(seed * 3) < 0.3 ? '#6f9a5e' : '#5f8f55';
    g.beginPath();
    g.arc(sx, sy - 3 - r * 0.8, r, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = dark ? 'rgba(140,170,130,0.12)' : 'rgba(214,232,176,0.35)';
    g.beginPath();
    g.arc(sx - r * 0.35, sy - 3 - r * 1.15, r * 0.45, 0, Math.PI * 2);
    g.fill();
  }

  /** 地铁:沿线画一条线(通车的实线,在修的虚线),站画一个小圆(没开的站空心,换乘站深色边)。颜色压着(见 11-美术.md),每条线一个。 */
  private subway(city: City) {
    const g = this.g;
    city.lines!.forEach((l, i) => {
      const c = lineColor(l.k ?? i + 1, false);
      g.save();
      g.strokeStyle = c;
      g.lineWidth = 3;
      g.lineCap = 'round';
      g.lineJoin = 'round';
      const draw = (from: number, to: number) => {
        if (to <= from) return;
        g.beginPath();
        l.path.slice(from, to + 1).forEach(([x, y], k) => {
          const [sx, sy] = this.iso(x + 0.5, y + 0.5);
          if (k === 0) g.moveTo(sx, sy);
          else g.lineTo(sx, sy);
        });
        g.stroke();
      };
      // 通了车的一段实线,在修的(整条、延长段)虚线
      const span = openSpan(l);
      const end = l.path.length - 1;
      g.globalAlpha = 0.5;
      g.setLineDash([6, 5]);
      if (span) {
        draw(0, span[0]);
        draw(span[1], end);
      } else draw(0, end);
      g.setLineDash([]);
      g.globalAlpha = 0.85;
      if (span) draw(span[0], span[1]);
      l.st.forEach(([x, y], k) => {
        const [sx, sy] = this.iso(x + 0.5, y + 0.5);
        const on = l.so ? l.so[k] : l.open;
        g.beginPath();
        g.arc(sx, sy, l.xs?.[k] ? 5.5 : 4.5, 0, Math.PI * 2);
        g.fillStyle = on ? '#f4efe6' : 'rgba(244,239,230,0.35)';
        g.fill();
        g.lineWidth = 2;
        g.strokeStyle = l.xs?.[k] && on ? '#3a342c' : c;
        g.stroke();
      });
      g.restore();
    });
  }

  /**
   * 地铁图(像地图软件的公交图):底图和楼盖一层纸色(夜里是暗色)压淡;线按线的颜色加粗、坐的人越多越粗,
   * 同一段上走着几条线就并排画开;站画白点,换乘站画大一点的白圈加深色边(隔一格的几个站连成一块);
   * 在修的线画虚线、写哪年通车;线的两头挂线号;站名按要紧的先写,挤不下的不写。都按屏幕像素画,缩放时字不糊。
   */
  private transitMap(city: City, sky: Sky, W: number, H: number, t: number) {
    const g = this.g;
    const night = sky.night > 0.5;
    g.fillStyle = night ? 'rgba(21,19,15,0.64)' : 'rgba(243,238,227,0.7)';
    g.fillRect(0, 0, W, H);
    const lines = city.lines ?? [];
    const z = Math.max(0.75, Math.min(1.35, this.cam.s));
    const ink = night ? '#efe8da' : '#26221c';
    const halo = night ? 'rgba(21,19,15,0.92)' : 'rgba(243,238,227,0.94)';
    const white = night ? '#f3eee3' : '#fffdf8';
    const edge = night ? '#0f0d0a' : '#3a342c';
    const P = (x: number, y: number) => this.toScreen(...this.iso(x + 0.5, y + 0.5));
    const col = (l: SubwayLine, i: number) => {
      const c = lineColor(l.k ?? i + 1, true);
      return night ? lighten(c, 0.22) : c;
    };
    const maxR = Math.max(1, ...lines.map((l) => l.riders ?? 0));
    const width = (l: SubwayLine) => (l.open ? 3.5 + 6 * Math.sqrt((l.riders ?? 0) / maxR) : 3) * z;
    // 同一段上走着几条线:并排画开(每条线在这段上挪开一点)
    const key = (a: [number, number], b: [number, number]) => {
      const p = `${a[0]},${a[1]}`, q = `${b[0]},${b[1]}`;
      return p < q ? `${p}|${q}` : `${q}|${p}`;
    };
    const share = new Map<string, number[]>();
    lines.forEach((l, i) => {
      for (let k = 1; k < l.path.length; k++) {
        const kk = key(l.path[k - 1], l.path[k]);
        const v = share.get(kk) ?? [];
        if (!v.includes(i)) v.push(i);
        share.set(kk, v);
      }
    });
    const shape = (l: SubwayLine, i: number): [number, number][] => {
      const raw = l.path.map(([x, y]) => P(x, y));
      const acc = raw.map(() => [0, 0, 0]);
      for (let k = 1; k < raw.length; k++) {
        const v = share.get(key(l.path[k - 1], l.path[k]))!;
        if (v.length < 2) continue;
        const o = (v.indexOf(i) - (v.length - 1) / 2) * 7 * z;
        const dx = raw[k][0] - raw[k - 1][0], dy = raw[k][1] - raw[k - 1][1];
        const len = Math.hypot(dx, dy) || 1;
        for (const j of [k - 1, k]) {
          acc[j][0] += (-dy / len) * o;
          acc[j][1] += (dx / len) * o;
          acc[j][2]++;
        }
      }
      return raw.map((p, k) => (acc[k][2] ? [p[0] + acc[k][0] / acc[k][2], p[1] + acc[k][1] / acc[k][2]] : p));
    };
    const shapes = lines.map(shape);
    const stroke = (pts: [number, number][], w: number, c: string) => {
      g.beginPath();
      pts.forEach(([x, y], k) => (k ? g.lineTo(x, y) : g.moveTo(x, y)));
      g.lineWidth = w;
      g.strokeStyle = c;
      g.stroke();
    };
    g.save();
    g.lineCap = 'round';
    g.lineJoin = 'round';
    // 在修的(整条线、延长段)先画虚线,压在通车的线下面
    const spans = lines.map(openSpan);
    lines.forEach((l, i) => {
      const sp = spans[i], end = shapes[i].length - 1;
      g.globalAlpha = 0.75;
      g.setLineDash([7 * z, 6 * z]);
      for (const [a, b] of sp ? [[0, sp[0]], [sp[1], end]] : [[0, end]]) if (b > a) stroke(shapes[i].slice(a, b + 1), 3 * z, col(l, i));
      g.setLineDash([]);
      g.globalAlpha = 1;
    });
    // 通车的一段:先描一圈底色,几条线交叉的地方分得开
    lines.forEach((l, i) => spans[i] && stroke(shapes[i].slice(spans[i]![0], spans[i]![1] + 1), width(l) + 3 * z, halo));
    lines.forEach((l, i) => spans[i] && stroke(shapes[i].slice(spans[i]![0], spans[i]![1] + 1), width(l), col(l, i)));
    // 站:换乘站(别的线的站在同一格或者隔一格)几个连成一块;别的画白点
    type St = { x: number; y: number; sx: number; sy: number; name: string; open: boolean; end: boolean; line: number };
    const all: St[] = [];
    lines.forEach((l, i) => l.st.forEach(([x, y], k) => {
      const at = l.path.findIndex(([px, py]) => px === x && py === y);
      const [sx, sy] = at >= 0 ? shapes[i][at] : P(x, y);
      all.push({ x, y, sx, sy, name: l.sn?.[k] ?? '', open: l.open && (l.so ? l.so[k] : true), end: k === 0 || k === l.st.length - 1, line: i });
    }));
    const xfer = all.filter((s) => all.some((o) => o.line !== s.line && Math.abs(o.x - s.x) + Math.abs(o.y - s.y) <= 1));
    for (const s of all) {
      if (xfer.includes(s)) continue;
      const l = lines[s.line];
      const r = width(l) / 2 + 1.6 * z;
      g.beginPath();
      g.arc(s.sx, s.sy, r, 0, Math.PI * 2);
      g.fillStyle = s.open ? white : halo;
      g.fill();
      g.lineWidth = 1.6 * z;
      g.strokeStyle = col(l, s.line);
      if (!s.open) g.setLineDash([2 * z, 2 * z]);
      g.stroke();
      g.setLineDash([]);
    }
    // 换乘站:按隔一格连成几块,每块画一个白底深边的圈(两三个站就是一条胶囊)
    const groups: St[][] = [];
    for (const s of xfer) {
      const hit = groups.filter((gr) => gr.some((o) => Math.abs(o.x - s.x) + Math.abs(o.y - s.y) <= 1));
      const merged = hit.flat().concat(s);
      for (const h of hit) groups.splice(groups.indexOf(h), 1);
      groups.push(merged);
    }
    const R = 6.2 * z;
    for (const gr of groups) {
      const open = gr.some((s) => s.open);
      const pts = gr.map((s) => [s.sx, s.sy] as [number, number]);
      const uniq = pts.filter((p, k) => pts.findIndex((q) => Math.hypot(q[0] - p[0], q[1] - p[1]) < 1) === k);
      // 还没开的换乘站:边淡一些、细一些
      const rim = open ? edge : night ? 'rgba(239,232,218,0.45)' : 'rgba(58,52,44,0.4)';
      const lw = (open ? 2.2 : 1.4) * z;
      if (uniq.length === 1) {
        g.beginPath();
        g.arc(uniq[0][0], uniq[0][1], open ? R : R * 0.8, 0, Math.PI * 2);
        g.fillStyle = open ? white : halo;
        g.fill();
        g.lineWidth = lw;
        g.strokeStyle = rim;
        g.stroke();
      } else {
        const r = open ? R : R * 0.8;
        stroke(uniq, r * 2 + lw, rim);
        stroke(uniq, r * 2 - lw, open ? white : halo);
      }
    }
    // 线号:线的两头挂一个小方牌
    g.font = `700 ${Math.round(10 * z)}px system-ui, -apple-system, "PingFang SC", sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    const placed: [number, number, number, number][] = [];
    const free = (x: number, y: number, w: number, h: number) =>
      x > 2 && y > 2 && x + w < W - 2 && y + h < H - 2 && !placed.some(([a, b, c, d]) => x < a + c && a < x + w && y < b + d && b < y + h);
    lines.forEach((l, i) => {
      const pts = shapes[i];
      if (pts.length < 2) return;
      for (const [a, b] of [[0, 1], [pts.length - 1, pts.length - 2]]) {
        const dx = pts[a][0] - pts[b][0], dy = pts[a][1] - pts[b][1];
        const len = Math.hypot(dx, dy) || 1;
        const x = pts[a][0] + (dx / len) * 16 * z, y = pts[a][1] + (dy / len) * 16 * z;
        const s = 15 * z;
        g.globalAlpha = l.open ? 1 : 0.75;
        g.fillStyle = col(l, i);
        g.beginPath();
        g.roundRect(x - s / 2, y - s / 2, s, s, 3.5 * z);
        g.fill();
        g.fillStyle = '#fffdf8';
        g.fillText(String(l.k ?? i + 1), x, y + 0.5);
        g.globalAlpha = 1;
        placed.push([x - s / 2, y - s / 2, s, s]);
      }
    });
    // 站名:换乘站、两头的站先写,镜头拉近了才写中间的站;挤不下的不写
    const label = (sx: number, sy: number, r: number, text: string, strong: boolean, color = ink) => {
      if (!text) return;
      g.font = `${strong ? 600 : 500} ${Math.round((strong ? 11.5 : 10.5) * z)}px system-ui, -apple-system, "PingFang SC", sans-serif`;
      const w = g.measureText(text).width, h = 13 * z;
      const spots: [number, number][] = [[sx + r + 4, sy - h / 2], [sx - r - 4 - w, sy - h / 2], [sx - w / 2, sy - r - 3 - h], [sx - w / 2, sy + r + 3]];
      for (const [x, y] of spots) {
        if (!free(x, y, w, h)) continue;
        placed.push([x, y, w, h]);
        g.textAlign = 'left';
        g.textBaseline = 'middle';
        g.lineWidth = 3;
        g.strokeStyle = halo;
        g.strokeText(text, x, y + h / 2);
        g.fillStyle = color;
        g.fillText(text, x, y + h / 2);
        return;
      }
    };
    for (const gr of groups) {
      const cx = gr.reduce((a, s) => a + s.sx, 0) / gr.length, cy = gr.reduce((a, s) => a + s.sy, 0) / gr.length;
      label(cx, cy, R + 2 * z, gr.find((s) => s.name)?.name ?? '', true);
    }
    for (const s of all) if (s.end && !xfer.includes(s)) label(s.sx, s.sy, width(lines[s.line]) / 2 + 2, s.name, true);
    if (this.cam.s >= 0.8) for (const s of all) if (!s.end && !xfer.includes(s)) label(s.sx, s.sy, width(lines[s.line]) / 2 + 2, s.name, false);
    // 在修的线、延长段:中间写哪年通车
    lines.forEach((l, i) => {
      const sp = spans[i], end = shapes[i].length - 1, k = l.k ?? i + 1;
      const year = (d: number | undefined) => `第 ${Math.floor((d ?? 0) / 360) + 1} 年通车`;
      if (!sp) {
        if (l.opens == null || end < 1) return;
        const p = shapes[i][Math.floor(end / 2)];
        label(p[0], p[1], width(l) / 2 + 3, `${k} 号线 · ${year(l.opens)}`, false, col(l, i));
        return;
      }
      for (const [a, b, d] of [[0, sp[0], l.sd?.[0]], [sp[1], end, l.sd?.[l.st.length - 1]]] as [number, number, number | undefined][]) {
        if (b <= a) continue;
        const p = shapes[i][Math.floor((a + b) / 2)];
        label(p[0], p[1], 5 * z, `${k} 号线延长段 · ${year(d)}`, false, col(l, i));
      }
    });
    g.restore();
    // 选中的地块框子画在最上面
    const sel = city.plots.find((p) => p.id === this.selected);
    if (sel) {
      g.save();
      g.translate(this.cam.x, this.cam.y);
      g.scale(this.cam.s, this.cam.s);
      this.outline(sel, t);
      g.restore();
    }
  }

  /** 选中的地块的金框;fill:框里也铺一层(地铁图上要,平常的地图上地面那一层已经铺过了)。 */
  private outline(p: Plot, t: number, fill = true) {
    const g = this.g;
    this.diamond(p.x, p.y, 0.02);
    if (fill) {
      g.fillStyle = 'rgba(224,178,76,0.16)';
      g.fill();
    }
    g.save();
    g.shadowColor = 'rgba(224,178,76,0.8)';
    g.shadowBlur = 8;
    g.strokeStyle = colors.gold;
    // 刚定位过来的:框子粗细跳两秒,找得到
    const k = (t - this.pulse) / 2000;
    g.lineWidth = k >= 0 && k < 1 ? 2.5 + 3 * Math.abs(Math.sin(k * Math.PI * 4)) * (1 - k) : 2.5;
    g.stroke();
    g.restore();
  }

  /** 一个等距的盒子:底面是地块里缩进 inset 的菱形,高 h。左面亮、右面暗(光从左上来)。 */
  private box(x: number, y: number, inset: number, h: number, wall: string, top?: string, shade = 1) {
    const g = this.g;
    const [ax, ay] = this.iso(x + inset, y + 1 - inset); // 左
    const [bx, by] = this.iso(x + 1 - inset, y + 1 - inset); // 前
    const [cx, cy] = this.iso(x + 1 - inset, y + inset); // 右
    const [dx, dy] = this.iso(x + inset, y + inset); // 后
    // 左面
    g.beginPath();
    g.moveTo(ax, ay);
    g.lineTo(bx, by);
    g.lineTo(bx, by - h);
    g.lineTo(ax, ay - h);
    g.closePath();
    g.fillStyle = wall;
    g.fill();
    // 右面(暗)
    g.beginPath();
    g.moveTo(bx, by);
    g.lineTo(cx, cy);
    g.lineTo(cx, cy - h);
    g.lineTo(bx, by - h);
    g.closePath();
    g.fillStyle = wall;
    g.fill();
    g.fillStyle = `rgba(0,0,0,${0.22 * shade})`;
    g.fill();
    // 顶面
    g.beginPath();
    g.moveTo(ax, ay - h);
    g.lineTo(bx, by - h);
    g.lineTo(cx, cy - h);
    g.lineTo(dx, dy - h);
    g.closePath();
    g.fillStyle = top ?? wall;
    g.fill();
    g.fillStyle = 'rgba(255,255,255,0.12)';
    g.fill();
    return { ax, ay, bx, by, cx, cy, dx, dy };
  }

  /** 楼面上一层层的窗:左右两个面各画。 */
  private windows(q: ReturnType<CityMap['box']>, floors: number, from: number, lit: string, glass: string, seed: number, night: number, fh = FH) {
    const g = this.g;
    const faces: [number, number, number, number][] = [
      [q.ax, q.ay, q.bx, q.by],
      [q.bx, q.by, q.cx, q.cy],
    ];
    for (let f = from; f < floors; f++) {
      const y0 = -(f + 0.35) * fh;
      faces.forEach(([x1, y1, x2, y2], fi) => {
        const n = 3;
        for (let i = 0; i < n; i++) {
          const t0 = (i + 0.25) / n;
          const t1 = (i + 0.75) / n;
          const on = hash(seed * 131 + f * 7 + i + fi * 3) < 0.12 + night * 0.5;
          g.strokeStyle = on && night > 0 ? lit : glass;
          g.lineWidth = fh * 0.45;
          g.beginPath();
          g.moveTo(x1 + (x2 - x1) * t0, y1 + (y2 - y1) * t0 + y0);
          g.lineTo(x1 + (x2 - x1) * t1, y1 + (y2 - y1) * t1 + y0);
          g.stroke();
        }
      });
    }
  }

  /**
   * 盖好的住宅、高层、写字楼:楼型定外立面(11-美术.md),同一楼型里按楼号挑变化——
   * 墙色的深浅、有没有裙楼、上面几层收不收进去、楼顶是什么样。返回主楼那一截的盒子(地面上的四个角)。
   */
  private tower(p: Plot, fa: { wall: string; glass: string; lit: string; roof: string }, sky: Sky): ReturnType<CityMap['box']> {
    const g = this.g;
    const id = p.projectId ?? p.id;
    const v = (k: number) => hash(id * 53 + k * 7919);
    const type = p.type ?? 'housing';
    const n = p.floors;
    const night = sky.night;
    const glasses = type === 'office' ? ['#5f7c92', '#557489', '#6a8597', '#4f6a7d'] : [fa.glass];
    const wall = pick(WALLS[type] ?? WALLS.housing, v(1));
    const glass = pick(glasses, v(2));
    // 裙楼:高层和写字楼多半有两三层的底座(商铺、大堂),住宅少数底层是一排商铺
    let base = 0;
    const podium = n >= 8 && v(3) < (type === 'housing' ? 0.35 : 0.7) ? (type === 'housing' ? 1 : 2 + Math.floor(v(4) * 2)) : 0;
    if (podium) {
      const pq = this.box(p.x, p.y, 0.08, podium * FH, type === 'office' ? '#c9cdd0' : '#d9cbb4');
      this.rows(pq, podium, 0, 2, 0.8, '#4d5d66', fa.lit, id * 3, night, false);
      base = podium;
    }
    const inset = type === 'office' ? 0.2 : type === 'highrise' ? 0.22 : 0.17;
    // 收进:十二层以上的,一半在六到八成高的地方往里收一截
    const cut = n >= 12 && v(5) < 0.5 ? Math.round(n * (0.6 + v(6) * 0.2)) : n;
    const segs: [number, number, number][] = [[base, cut, inset]];
    if (cut < n) segs.push([cut, n, inset + 0.07]);
    let q = this.box(p.x, p.y, inset, 0, wall);
    for (const [f0, f1, ins] of segs) {
      if (f1 <= f0) continue;
      g.save();
      g.translate(0, -f0 * FH);
      q = this.box(p.x, p.y, ins, (f1 - f0) * FH, wall);
      this.skin(type, q, f1 - f0, glass, fa.lit, id + f0 * 17, night, v(8) < 0.5);
      g.restore();
    }
    // 楼顶
    g.save();
    g.translate(0, -n * FH);
    this.crown(type, p, v, wall, sky);
    g.restore();
    return q;
  }

  /** 一截楼身的外立面。 */
  private skin(type: string, q: ReturnType<CityMap['box']>, floors: number, glass: string, lit: string, seed: number, night: number, odd: boolean) {
    const g = this.g;
    if (type === 'office' || type === 'landmark') {
      // 玻璃幕墙:两个面铺玻璃,竖向分格,每层一条深色层间带,左面一道天光反射
      const faces: [number, number, number, number, number][] = [
        [q.ax, q.ay, q.bx, q.by, 0],
        [q.bx, q.by, q.cx, q.cy, 0.25],
      ];
      const h = floors * FH;
      for (const [x1, y1, x2, y2, dark] of faces) {
        g.beginPath();
        g.moveTo(x1, y1);
        g.lineTo(x2, y2);
        g.lineTo(x2, y2 - h);
        g.lineTo(x1, y1 - h);
        g.closePath();
        g.fillStyle = glass;
        g.fill();
        if (dark) {
          g.fillStyle = `rgba(0,0,0,${dark})`;
          g.fill();
        }
        g.strokeStyle = 'rgba(255,255,255,0.22)';
        g.lineWidth = 0.6;
        for (let i = 1; i < 6; i++) {
          const t = i / 6;
          g.beginPath();
          g.moveTo(x1 + (x2 - x1) * t, y1 + (y2 - y1) * t);
          g.lineTo(x1 + (x2 - x1) * t, y1 + (y2 - y1) * t - h);
          g.stroke();
        }
        g.strokeStyle = type === 'landmark' ? 'rgba(224,178,76,0.55)' : 'rgba(28,38,48,0.35)';
        g.lineWidth = 0.9;
        for (let f = 1; f < floors; f++) {
          g.beginPath();
          g.moveTo(x1, y1 - f * FH);
          g.lineTo(x2, y2 - f * FH);
          g.stroke();
        }
        // 夜里亮着的格子
        if (night > 0) {
          g.fillStyle = lit;
          for (let f = 0; f < floors; f++) {
            for (let i = 0; i < 6; i++) {
              if (hash(seed * 131 + f * 11 + i + (dark ? 97 : 0)) < night * 0.45) {
                const t = (i + 0.5) / 6;
                g.fillRect(x1 + (x2 - x1) * t - 1.5, y1 + (y2 - y1) * t - (f + 0.8) * FH, 3, FH * 0.6);
              }
            }
          }
        }
      }
      // 天光反射
      g.fillStyle = 'rgba(255,255,255,0.13)';
      g.beginPath();
      const at = (t: number, up: number) => [q.ax + (q.bx - q.ax) * t, q.ay + (q.by - q.ay) * t - up] as const;
      const [p1x, p1y] = at(0.18, 0);
      const [p2x, p2y] = at(0.3, 0);
      const [p3x, p3y] = at(0.55, h);
      const [p4x, p4y] = at(0.43, h);
      g.moveTo(p1x, p1y);
      g.lineTo(p2x, p2y);
      g.lineTo(p3x, p3y);
      g.lineTo(p4x, p4y);
      g.closePath();
      g.fill();
      return;
    }
    if (type === 'highrise') {
      // 高层:窄高的窗,一面四扇
      this.rows(q, floors, 0, 4, 0.5, glass, lit, seed, night, false);
      return;
    }
    // 住宅:一面三扇,隔一扇一个阳台
    this.rows(q, floors, 0, 3, 0.5, glass, lit, seed, night, true, odd);
  }

  /** 一层层的窗:每面 per 扇,占一格的 share;balcony 的话隔一扇窗底下一道阳台栏杆。 */
  private rows(q: ReturnType<CityMap['box']>, floors: number, from: number, per: number, share: number, glass: string, lit: string, seed: number, night: number, balcony: boolean, odd = false) {
    const g = this.g;
    const faces: [number, number, number, number][] = [
      [q.ax, q.ay, q.bx, q.by],
      [q.bx, q.by, q.cx, q.cy],
    ];
    for (let f = from; f < floors; f++) {
      const y0 = -(f + 0.4) * FH;
      faces.forEach(([x1, y1, x2, y2], fi) => {
        for (let i = 0; i < per; i++) {
          const t0 = (i + 0.5 - share / 2) / per;
          const t1 = (i + 0.5 + share / 2) / per;
          const on = hash(seed * 131 + f * 7 + i + fi * 3) < 0.12 + night * 0.5;
          g.strokeStyle = on && night > 0 ? lit : glass;
          g.lineWidth = FH * 0.5;
          g.beginPath();
          g.moveTo(x1 + (x2 - x1) * t0, y1 + (y2 - y1) * t0 + y0);
          g.lineTo(x1 + (x2 - x1) * t1, y1 + (y2 - y1) * t1 + y0);
          g.stroke();
          if (balcony && (i % 2 === 0) !== odd) {
            g.strokeStyle = 'rgba(255,255,255,0.6)';
            g.lineWidth = 0.9;
            g.beginPath();
            g.moveTo(x1 + (x2 - x1) * (t0 - 0.03), y1 + (y2 - y1) * (t0 - 0.03) + y0 + FH * 0.42);
            g.lineTo(x1 + (x2 - x1) * (t1 + 0.03), y1 + (y2 - y1) * (t1 + 0.03) + y0 + FH * 0.42);
            g.stroke();
          }
        }
      });
    }
  }

  /** 楼顶:住宅有电梯机房;高层平顶、阶梯顶或天线;写字楼平顶、斜顶或尖顶。坐标已经挪到楼顶。 */
  private crown(type: string, p: Plot, v: (k: number) => number, wall: string, sky: Sky) {
    const g = this.g;
    const k = v(7);
    const [cx, cy] = this.iso(p.x + 0.5, p.y + 0.5);
    if (type === 'housing') {
      const r = v(9);
      if (r < 0.3) {
        // 屋顶花园
        this.quad(p.x + 0.21, p.y + 0.56, p.x + 0.5, p.y + 0.8);
        g.fillStyle = '#86a86f';
        g.fill();
        this.tree(p.x + 0.3, p.y + 0.72, sky, p.id * 7);
      } else if (r < 0.55) {
        // 太阳能板:深蓝的板子,细白的格
        this.quad(p.x + 0.55, p.y + 0.22, p.x + 0.8, p.y + 0.46);
        g.fillStyle = '#3d4f66';
        g.fill();
        g.strokeStyle = 'rgba(220,230,240,0.45)';
        g.lineWidth = 0.5;
        g.beginPath();
        this.seg(p.x + 0.675, p.y + 0.22, p.x + 0.675, p.y + 0.46);
        this.seg(p.x + 0.55, p.y + 0.34, p.x + 0.8, p.y + 0.34);
        g.stroke();
      } else {
        // 水箱
        this.solid(p.x + 0.22, p.y + 0.22, p.x + 0.33, p.y + 0.33, 7, '#c9c3b8', '#a8a49d');
      }
      this.box(p.x, p.y, 0.36, 5, wall);
      if (k < 0.5) this.box(p.x + 0.12, p.y - 0.1, 0.42, 4, '#9d978f');
      return;
    }
    if (type === 'highrise') {
      if (k < 0.33) {
        this.box(p.x, p.y, 0.3, 6, wall);
        this.box(p.x, p.y, 0.37, 10, wall);
      } else if (k < 0.66) {
        this.box(p.x, p.y, 0.34, 5, '#a8a49d');
        g.strokeStyle = colors.concreteDark;
        g.lineWidth = 1;
        g.beginPath();
        g.moveTo(cx, cy);
        g.lineTo(cx, cy - 20);
        g.stroke();
      } else {
        this.box(p.x, p.y, 0.33, 4, '#a8a49d');
      }
      return;
    }
    if (type === 'office' || type === 'landmark') {
      if (k < 0.4) {
        // 斜顶:朝前的一面削成斜的
        g.fillStyle = '#6f7d89';
        const [ax, ay] = this.iso(p.x + 0.2, p.y + 0.8);
        const [bx, by] = this.iso(p.x + 0.8, p.y + 0.8);
        const [ex, ey] = this.iso(p.x + 0.8, p.y + 0.2);
        g.beginPath();
        g.moveTo(ax, ay);
        g.lineTo(bx, by);
        g.lineTo(ex, ey - 14);
        g.lineTo((ax + ex) / 2, (ay + ey) / 2 - 14);
        g.closePath();
        g.fill();
      } else if (k < 0.7 || type === 'landmark') {
        this.box(p.x, p.y, 0.34, 6, '#6f7d89');
        g.strokeStyle = type === 'landmark' ? colors.gold : '#b7bdc4';
        g.lineWidth = 1.2;
        g.beginPath();
        g.moveTo(cx, cy - 6);
        g.lineTo(cx, cy - 30);
        g.stroke();
      } else {
        this.box(p.x, p.y, 0.3, 5, '#6f7d89');
      }
    }
  }

  /**
   * 机构和地标各画各的样子(11-美术.md:公共建筑一眼认得出):学校红顶带旗杆、医院白楼红十字、派出所蓝灰楼顶上蓝灯、
   * 市政大楼石材立面加柱廊、电视塔细塔身加球冠和天线、体育场椭圆看台、大剧院贝壳顶、会展中心弧形大屋顶。
   * 不是这几种的返回 null(按普通的楼画)。
   */
  private landmark(p: Plot, sky: Sky, t: number): number | null {
    const g = this.g;
    const [cx, cy] = this.iso(p.x + 0.5, p.y + 0.5);
    const type = p.type ?? '';
    const lit = sky.night > 0;
    switch (type) {
      case 'school': {
        const h = 3 * FH;
        const q = this.box(p.x, p.y, 0.14, h, '#e7cfa4');
        this.rows(q, 3, 0, 4, 0.55, '#58717f', '#ffd990', p.id * 7, sky.night, false);
        g.fillStyle = '#b5553d';
        g.beginPath();
        g.moveTo(q.ax, q.ay - h);
        g.lineTo((q.ax + q.cx) / 2, (q.ay + q.cy) / 2 - h - 8);
        g.lineTo(q.cx, q.cy - h);
        g.lineTo(q.bx, q.by - h);
        g.closePath();
        g.fill();
        // 旗杆
        const [fx, fy] = this.iso(p.x + 0.9, p.y + 0.95);
        g.strokeStyle = '#8f8a82';
        g.lineWidth = 1;
        g.beginPath();
        g.moveTo(fx, fy);
        g.lineTo(fx, fy - 26);
        g.stroke();
        g.fillStyle = '#b23b2e';
        g.fillRect(fx, fy - 26, 8, 5);
        return h + 8;
      }
      case 'hospital': {
        const h = Math.max(4, p.floors) * FH;
        const q = this.box(p.x, p.y, 0.15, h, '#f2efe8');
        this.rows(q, Math.max(4, p.floors), 0, 4, 0.5, '#6d8a9c', '#fff2c8', p.id * 5, sky.night, false);
        // 红十字
        const x = (q.ax + q.bx) / 2, y = (q.ay + q.by) / 2 - h * 0.62;
        g.fillStyle = '#c0392b';
        g.fillRect(x - 5, y - 1.5, 10, 3);
        g.fillRect(x - 1.5, y - 5, 3, 10);
        return h;
      }
      case 'police': {
        const h = 3 * FH;
        const q = this.box(p.x, p.y, 0.18, h, '#9fb0c0');
        this.rows(q, 3, 0, 3, 0.5, '#3f5569', '#fbe8a8', p.id * 3, sky.night, false);
        g.fillStyle = Math.sin(t / 300) > 0 || !lit ? '#3b6fd8' : '#d84a3b';
        g.beginPath();
        g.arc(cx, cy - h - 4, 2.5, 0, Math.PI * 2);
        g.fill();
        return h + 4;
      }
      case 'cityhall': {
        const n = Math.max(6, p.floors);
        const h = n * FH;
        // 裙楼加柱廊,主楼石材立面
        const pq = this.box(p.x, p.y, 0.06, FH * 2, '#ddd2bd');
        g.strokeStyle = 'rgba(255,255,255,0.7)';
        g.lineWidth = 1.2;
        for (let i = 1; i < 8; i++) {
          const tt = i / 8;
          const x = pq.ax + (pq.bx - pq.ax) * tt, y = pq.ay + (pq.by - pq.ay) * tt;
          g.beginPath();
          g.moveTo(x, y);
          g.lineTo(x, y - FH * 2);
          g.stroke();
        }
        g.save();
        g.translate(0, -FH * 2);
        const q = this.box(p.x, p.y, 0.18, h - FH * 2, '#e4d8c2');
        this.rows(q, n - 2, 0, 4, 0.45, '#4d6778', '#ffe2a0', p.id, sky.night, false);
        g.restore();
        g.save();
        g.translate(0, -h);
        this.box(p.x, p.y, 0.34, 6, '#cfc3ad');
        g.restore();
        g.fillStyle = colors.gold;
        g.fillRect(cx - 1, cy - h - 24, 2, 18);
        g.fillStyle = '#b23b2e';
        g.fillRect(cx + 1, cy - h - 24, 9, 6);
        return h + 20;
      }
      case 'tvtower': {
        // 矮楼那一档塔也压低(最矮三十层的塔身按压低后的层数画)
        const h = Math.max(this.relief === 1 ? lowFloors(30) : 30, p.floors) * FH;
        // 三条腿的塔座、细塔身、球冠、天线和红色航空灯
        this.box(p.x, p.y, 0.3, FH * 3, '#b8b3aa');
        g.save();
        g.translate(0, -FH * 3);
        this.box(p.x, p.y, 0.45, h - FH * 3, '#d9d4cb');
        g.restore();
        const podY = cy - h * 0.82;
        g.fillStyle = '#c9c3b8';
        g.beginPath();
        g.ellipse(cx, podY, 13, 9, 0, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = 'rgba(0,0,0,0.18)';
        g.beginPath();
        g.ellipse(cx + 3, podY + 1, 10, 7, 0, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = lit ? '#ffd98f' : '#5f7c92';
        g.fillRect(cx - 11, podY - 1.5, 22, 3);
        g.strokeStyle = '#8f8a82';
        g.lineWidth = 1.4;
        g.beginPath();
        g.moveTo(cx, cy - h);
        g.lineTo(cx, cy - h - 30);
        g.stroke();
        g.fillStyle = Math.sin(t / 500) > 0 ? '#e0402e' : '#7a2a20';
        g.beginPath();
        g.arc(cx, cy - h - 30, 2.2, 0, Math.PI * 2);
        g.fill();
        return h + 30;
      }
      case 'stadium': {
        // 椭圆看台、绿色球场、几根灯架
        const rx = TW * 0.42, ry = TH * 0.42, wall = 14;
        g.fillStyle = '#b8b3aa';
        g.beginPath();
        g.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = '#d9d4cb';
        g.beginPath();
        g.ellipse(cx, cy - wall, rx, ry, 0, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = '#8f8a82';
        g.fillRect(cx - rx, cy - wall, rx * 2, wall);
        g.fillStyle = '#d9d4cb';
        g.beginPath();
        g.ellipse(cx, cy - wall, rx, ry, 0, 0, Math.PI);
        g.fill();
        g.fillStyle = '#6a9f62';
        g.beginPath();
        g.ellipse(cx, cy - wall + 1, rx * 0.66, ry * 0.62, 0, 0, Math.PI * 2);
        g.fill();
        g.strokeStyle = 'rgba(255,255,255,0.8)';
        g.lineWidth = 0.8;
        g.beginPath();
        g.ellipse(cx, cy - wall + 1, rx * 0.2, ry * 0.2, 0, 0, Math.PI * 2);
        g.stroke();
        for (const s of [-1, 1]) {
          g.strokeStyle = '#8f8a82';
          g.beginPath();
          g.moveTo(cx + s * rx * 0.9, cy - wall);
          g.lineTo(cx + s * rx * 0.9, cy - wall - 22);
          g.stroke();
          g.fillStyle = lit ? '#fff4d0' : '#cfd4d8';
          g.fillRect(cx + s * rx * 0.9 - 4, cy - wall - 25, 8, 3);
        }
        return wall + 22;
      }
      case 'theater': {
        // 低矮的基座上几片白色的贝壳顶
        const base = FH * 2;
        this.box(p.x, p.y, 0.12, base, '#d9cbb4');
        g.fillStyle = '#f4f1ea';
        for (let i = 0; i < 3; i++) {
          const ox = (i - 1) * 12, sz = 16 - Math.abs(i - 1) * 4;
          g.beginPath();
          g.moveTo(cx + ox - sz, cy - base);
          g.quadraticCurveTo(cx + ox - sz * 0.2, cy - base - sz * 2.2, cx + ox + sz, cy - base);
          g.closePath();
          g.fill();
          g.strokeStyle = 'rgba(0,0,0,0.12)';
          g.lineWidth = 0.8;
          g.stroke();
        }
        if (lit) {
          g.fillStyle = 'rgba(255,217,143,0.8)';
          g.fillRect(cx - 18, cy - base + 2, 36, 3);
        }
        return base + 32;
      }
      case 'expo': {
        // 宽大的玻璃盒子加一条弧形大屋顶
        const h = FH * 4;
        const q = this.box(p.x, p.y, 0.06, h, '#7c95a8');
        this.skin('office', q, 4, '#5f7c92', '#f7e2a8', p.id * 11, sky.night, false);
        g.fillStyle = '#e8e2d6';
        g.beginPath();
        g.moveTo(q.ax - 3, q.ay - h);
        g.quadraticCurveTo((q.ax + q.cx) / 2, (q.ay + q.cy) / 2 - h - 26, q.cx + 3, q.cy - h);
        g.lineTo(q.bx, q.by - h + 3);
        g.closePath();
        g.fill();
        g.strokeStyle = 'rgba(0,0,0,0.15)';
        g.stroke();
        return h + 22;
      }
      default:
        return null;
    }
  }

  /** 商会的旗:插在地块靠前的角上(自己的楼顶上另有一面金旗)。 */
  private guildFlag(p: Plot) {
    const g = this.g;
    const [x, y] = this.iso(p.x + 0.12, p.y + 0.88);
    g.strokeStyle = '#3b3328';
    g.lineWidth = 1.2;
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x, y - 16);
    g.stroke();
    g.fillStyle = p.guild!;
    g.beginPath();
    g.moveTo(x, y - 16);
    g.lineTo(x + 9, y - 13);
    g.lineTo(x, y - 10);
    g.closePath();
    g.fill();
  }

  private building(p: Plot, sky: Sky, t: number) {
    const g = this.g;
    const [cx, cy] = this.iso(p.x + 0.5, p.y + 0.5);
    if (p.kind === 'public') {
      // 公共建筑也是玩家盖的:没盖好之前机构在板房里办公,盖好以后画真的楼,挂机构的牌子。
      const built = p.projectId && p.stage !== 'collapsed' && !p.closed;
      if (!built) {
        this.box(p.x, p.y, 0.3, 8, '#9fb6c8', '#d0dde6');
        if (p.open) this.sign(cx + 14, cy + 4, '招标');
        this.nameplate(p.publicName ?? '', cx, cy, 10, true);
        return;
      }
      const special = this.landmark(p, sky, t);
      const h = special ?? this.project(p, sky, t);
      this.nameplate(p.publicName ?? '', cx, cy, h + 4, false);
      return;
    }
    if (p.open && !p.projectId) return this.sign(cx, cy, '招标');
    const h = this.project(p, sky, t);
    // 银行的楼挂牌子
    if (p.label) this.nameplate(p.label, cx, cy, (h ?? 20) + 4, false);
  }

  /**
   * 平面那一档:楼不立起来,地上只画它占的那一块,颜色是从上往下看见的楼顶(墙色、瓦色、铁皮顶),描一道细边;
   * 工地描一圈安全黄的虚线,烂尾、有裂缝的描暖红,机构和银行照样挂名牌。地块全露出来,也还认得出哪块地上是什么楼。
   * 本来就贴着地的(围挡、废墟、招标牌)照立体的画。开着地图模式只描边不上色:楼顶的颜色会把地块的颜色盖掉一大块。
   */
  private footprint(p: Plot, sky: Sky, t: number) {
    const g = this.g;
    const [cx, cy] = this.iso(p.x + 0.5, p.y + 0.5);
    const bare = !!this.overlay;
    const pad = (inset: number, fill: string, edge = bare ? 'rgba(46,38,28,0.9)' : 'rgba(46,38,28,0.32)', dash = false) => {
      this.quad(p.x + inset, p.y + inset, p.x + 1 - inset, p.y + 1 - inset);
      if (!bare) {
        g.fillStyle = fill;
        g.fill();
      }
      g.strokeStyle = edge;
      g.lineWidth = dash ? 1.4 : 0.8;
      if (dash) g.setLineDash([3, 2]);
      g.stroke();
      if (dash) g.setLineDash([]);
    };
    if (p.kind === 'public') {
      const built = p.projectId && p.stage !== 'collapsed' && !p.closed;
      if (!built) {
        pad(0.3, '#d0dde6');
        if (p.open) this.sign(cx + 14, cy + 4, '招标');
      } else {
        switch (p.type) {
          case 'school': pad(0.14, '#b5553d'); break;
          case 'hospital':
            pad(0.15, '#f2efe8');
            g.fillStyle = '#c0392b';
            g.fillRect(cx - 4, cy - 1.2, 8, 2.4);
            g.fillRect(cx - 1.2, cy - 4, 2.4, 8);
            break;
          case 'police': pad(0.18, '#9fb0c0'); break;
          case 'cityhall': pad(0.06, '#ddd2bd'); pad(0.18, '#e4d8c2'); break;
          case 'tvtower':
            pad(0.3, '#b8b3aa');
            if (bare) break;
            g.fillStyle = '#c9c3b8';
            g.beginPath();
            g.ellipse(cx, cy, 13, 9, 0, 0, Math.PI * 2);
            g.fill();
            break;
          case 'stadium':
            g.beginPath();
            g.ellipse(cx, cy, TW * 0.42, TH * 0.42, 0, 0, Math.PI * 2);
            if (bare) {
              g.strokeStyle = 'rgba(46,38,28,0.9)';
              g.lineWidth = 0.8;
              g.stroke();
              break;
            }
            g.fillStyle = '#d9d4cb';
            g.fill();
            g.fillStyle = '#6a9f62';
            g.beginPath();
            g.ellipse(cx, cy, TW * 0.28, TH * 0.26, 0, 0, Math.PI * 2);
            g.fill();
            break;
          case 'theater': pad(0.12, '#d9cbb4'); pad(0.24, '#f4f1ea'); break;
          case 'expo': pad(0.06, '#e8e2d6'); break;
          default: this.flatProject(p, cx, cy, pad);
        }
      }
      this.nameplate(p.publicName ?? '', cx, cy, 2, !built);
      return;
    }
    if (p.open && !p.projectId) return this.sign(cx, cy, '招标');
    if (p.projectId ? p.stage === 'collapsed' || p.floors === 0 : p.kind !== 'hq') {
      this.project(p, sky, t);
      return;
    }
    this.flatProject(p, cx, cy, pad);
    if (p.label) this.nameplate(p.label, cx, cy, 2, false);
  }

  /** 平面那一档的一栋楼(承包的、总部、普通的公共建筑):占地和楼顶的颜色,和立体画出来的对得上。 */
  private flatProject(p: Plot, cx: number, cy: number, pad: (inset: number, fill: string, edge?: string, dash?: boolean) => void) {
    if (!p.projectId) {
      // 还没盖的总部:板房
      pad(0.34, '#d0dde6');
      if (p.mine) this.flag(cx, cy - 2);
      return;
    }
    const id = p.projectId;
    const stage = p.stage ?? '';
    const done = stage === 'delivered' || stage === 'acceptance';
    const stalled = p.closed || stage === 'closed' || !!p.stalled;
    const low = p.type !== 'works' && p.floors <= 3;
    const fa = facade[p.type ?? 'housing'] ?? facade.housing;
    const tall = done && !stalled && ((p.type === 'housing' && !low) || p.type === 'highrise' || p.type === 'office' || p.type === 'landmark');
    let inset = p.type === 'shophouse' ? 0.25 : 0.2;
    let top = stalled ? '#a8a49d' : done || stage === 'finishing' ? (p.facade ?? fa.wall) : colors.concrete;
    if (tall) {
      inset = p.type === 'office' ? 0.2 : p.type === 'highrise' ? 0.22 : 0.17;
      top = pick(WALLS[p.type ?? 'housing'] ?? WALLS.housing, hash(id * 53 + 7919));
    } else if (done && !stalled && p.type === 'works') {
      inset = 0.08;
      top = p.use === 'factory' ? '#9aa1a4' : p.use === 'plant' ? '#b3aea4' : '#8f979b';
    } else if (done && p.type === 'villa') {
      top = pick(VILLA_ROOFS, hash(id * 29 + 3));
    } else if (done && !stalled && low && (p.use ?? 'home') === 'home') {
      top = pick(ROOFS, hash(id * 29 + 5));
    }
    const widths = p.widths ?? [];
    if (widths.some((w) => Math.abs(w - 1) > 0.01)) inset = Math.max(0.02, Math.min(0.45, 0.5 - 0.3 * Math.min(1.5, Math.max(...widths))));
    const site = !stalled && (stage === 'structure' || stage === 'caps' || stage === 'finishing');
    pad(inset, top, stalled || p.cracked ? colors.bad : site ? '#e8b030' : undefined, site);
    if (p.mine) this.flag(cx, cy - 2);
  }

  /** 画一块地上的楼(承包的、总部、公共建筑),返回画了多高。 */
  private project(p: Plot, sky: Sky, t: number): number {
    const g = this.g;
    const [cx, cy] = this.iso(p.x + 0.5, p.y + 0.5);
    if (!p.projectId) {
      if (p.kind === 'hq') {
        // 还没盖的总部:一间板房
        this.box(p.x, p.y, 0.34, 7, '#9fb6c8', '#d0dde6');
        if (p.mine) this.flag(cx, cy - 12);
      }
      if (p.kind === 'dev') {
        // 买下还没开工的地:一圈围挡
        g.strokeStyle = p.mine ? colors.gold : 'rgba(120,110,95,0.8)';
        g.lineWidth = 1.5;
        this.diamond(p.x, p.y, 0.1);
        g.stroke();
        if (p.mine) this.flag(cx, cy - 4);
      }
      return 7;
    }
    const base = facade[p.type ?? 'housing'] ?? facade.housing;
    const fa = p.facade ? { ...base, wall: p.facade } : base;
    const stage = p.stage ?? '';
    if (stage === 'collapsed') {
      for (let i = 0; i < 9; i++) {
        const ox = (hash(p.id * 13 + i) - 0.5) * TW * 0.45;
        const oy = (hash(p.id * 17 + i) - 0.5) * TH * 0.4;
        g.fillStyle = i % 3 ? colors.concreteDark : colors.concrete;
        g.fillRect(cx + ox - 4, cy + oy - 5, 8, 5);
      }
      return 5;
    }
    if (p.floors === 0 && stage === 'prep') {
      // 发了包还没人接:只有一圈围挡
      g.strokeStyle = 'rgba(226,168,44,0.7)';
      g.lineWidth = 1.2;
      g.setLineDash([3, 2]);
      this.diamond(p.x, p.y, 0.12);
      g.stroke();
      g.setLineDash([]);
      return 4;
    }
    if (p.floors === 0) {
      // 开工准备、桩基:围挡和打桩机
      g.strokeStyle = '#e2a82c';
      g.lineWidth = 2;
      this.diamond(p.x, p.y, 0.12);
      g.stroke();
      g.fillStyle = colors.steelDark;
      g.fillRect(cx - 1.5, cy - 26, 3, 24);
      g.fillStyle = colors.steel;
      g.fillRect(cx - 5, cy - 6, 10, 5);
      if (p.mine) this.flag(cx + 10, cy - 10);
      return 24;
    }
    // 厂房一层比住宅高(六米上下);一到三层的矮楼一层画高四成(地图上一层只有四个多像素,地块七十多像素宽,
    // 矮楼原样画是一张饼,看着不像房子)
    const low = p.type !== 'works' && p.floors <= 3;
    const h = p.floors * FH * (p.type === 'works' ? 1.6 : low ? 1.4 : 1);
    const building = stage === 'structure' || stage === 'caps';
    const finishing = stage === 'finishing';
    const done = stage === 'delivered' || stage === 'acceptance';
    const stalled = p.closed || stage === 'closed' || !!p.stalled;
    const wall = stalled ? '#9a968f' : done || finishing ? fa.wall : colors.concrete;
    const shaped = (p.widths ?? []).some((w) => Math.abs(w - 1) > 0.01);
    if (shaped) {
      // 自己画的楼:一层一层叠,宽的层往外挑
      for (let f = 0; f < p.floors; f++) {
        const w = Math.min(1.5, p.widths?.[f] ?? 1);
        const inset = Math.max(0.02, Math.min(0.45, 0.5 - 0.3 * w));
        g.save();
        g.translate(0, -f * FH);
        this.box(p.x, p.y, inset, FH, wall, stalled ? '#a8a49d' : undefined);
        g.restore();
      }
    }
    if (shaped) {
      if (p.mine) this.flag(cx, cy - h - 8);
      return h;
    }
    // 地基不够的楼整栋微微歪(按真实的度数,不夸张)
    const leaning = (p.lean ?? 0) > 0;
    if (leaning) {
      const [fx, fy] = this.iso(p.x + 0.8, p.y + 0.8);
      g.save();
      g.translate(fx, fy);
      g.rotate(((p.lean ?? 0) * Math.PI) / 180);
      g.translate(-fx, -fy);
    }
    const tall = done && !stalled && ((p.type === 'housing' && !low) || p.type === 'highrise' || p.type === 'office' || p.type === 'landmark');
    const shed = done && !stalled && p.type === 'works';
    // 店屋占地小(一层一百平方米),别的矮楼照旧
    const inset = p.type === 'shophouse' ? 0.25 : 0.2;
    const q = shed ? this.works(p, sky, t) : tall ? this.tower(p, fa, sky) : this.box(p.x, p.y, inset, h, wall, stalled ? '#a8a49d' : undefined);
    if (tall || shed) {
      // 外立面已经在 tower、works 里画了
    } else if (!stalled && (done || finishing)) {
      this.windows(q, p.floors, 0, fa.lit, fa.glass, p.id, done ? sky.night : 0, h / Math.max(1, p.floors));
    } else {
      // 结构:楼板线
      g.strokeStyle = 'rgba(0,0,0,0.15)';
      g.lineWidth = 0.6;
      for (let f = 1; f < p.floors; f++) {
        g.beginPath();
        g.moveTo(q.ax, q.ay - f * FH);
        g.lineTo(q.bx, q.by - f * FH);
        g.lineTo(q.cx, q.cy - f * FH);
        g.stroke();
      }
    }
    if (building || finishing) {
      // 安全网和塔吊
      g.fillStyle = colors.net;
      g.beginPath();
      g.moveTo(q.ax - 2, q.ay + 1);
      g.lineTo(q.bx, q.by + 2);
      g.lineTo(q.cx + 2, q.cy + 1);
      g.lineTo(q.cx + 2, q.cy - h - 3);
      g.lineTo(q.bx, q.by - h - 2);
      g.lineTo(q.ax - 2, q.ay - h - 3);
      g.closePath();
      if (building) g.fill();
      if (building) this.crane(q.dx + 4, q.dy, h + 22, this.ct * 1000 + p.id * 997);
    }
    if (done && !stalled && p.type === 'shophouse' && (p.use === 'retail' || p.use === 'dining' || !p.use)) {
      this.storefront(q, p, h / Math.max(1, p.floors), sky);
    }
    // 一到三层的住家:双坡屋顶(屋脊顺着哪边、瓦的颜色按楼号挑),不再是平顶方盒子;洋房有自己的四坡顶
    const pitched = done && !stalled && !tall && !shed && p.type !== 'villa' && (p.use ?? 'home') === 'home' && low;
    if (pitched) {
      const id = p.projectId ?? p.id;
      const roof = pick(ROOFS, hash(id * 29 + 5));
      const e = inset - 0.03;
      this.gable(p.x + e, p.y + e, p.x + 1 - e, p.y + 1 - e, h, 8, roof, wall, hash(id * 13 + 1) < 0.5);
    } else if (done && !stalled && !tall && !shed && p.type !== 'villa') {
      // 平顶:一圈女儿墙,楼顶上的空调外机、水箱、太阳能板(按楼号挑)
      this.flatRoof(p, inset, h);
    }
    if (done && p.type === 'villa') {
      // 四坡顶:屋脊在正中,朝左的两面亮、朝右的暗(光从左上来),屋檐比墙挑出一点
      const roof = pick(VILLA_ROOFS, hash((p.projectId ?? p.id) * 29 + 3));
      const [ex, ey] = [(q.ax + q.cx) / 2, (q.ay + q.cy) / 2 - h - 9];
      const eave = (x: number, y: number): [number, number] => [x + (x - ex) * 0.08, y - h + 1];
      const A = eave(q.ax, q.ay), B = eave(q.bx, q.by), C = eave(q.cx, q.cy), D = eave(q.dx, q.dy);
      const faces: [[number, number], [number, number], number][] = [
        [D, A, 0.06], [A, B, 0], [B, C, 0.22], [C, D, 0.12],
      ];
      for (const [u, w, dark] of faces) {
        g.beginPath();
        g.moveTo(u[0], u[1]);
        g.lineTo(w[0], w[1]);
        g.lineTo(ex, ey);
        g.closePath();
        g.fillStyle = roof;
        g.fill();
        if (dark) {
          g.fillStyle = `rgba(0,0,0,${dark})`;
          g.fill();
        }
      }
    }
    if (p.mine) this.flag((q.bx + q.dx) / 2, (q.by + q.dy) / 2 - h - 4);
    if (p.stalled) {
      // 烂尾:楼身拉一条红横幅
      g.fillStyle = '#b23b2e';
      g.beginPath();
      g.moveTo(q.ax + 2, q.ay - h * 0.55);
      g.lineTo(q.bx, q.by - h * 0.55);
      g.lineTo(q.bx, q.by - h * 0.55 - 5);
      g.lineTo(q.ax + 2, q.ay - h * 0.55 - 5);
      g.closePath();
      g.fill();
    }
    // 看得见的毛病:墙面上几道锈水、麻面的暗痕;顶上渗水的,顶层一片水渍(远看只是脏,凑近才看得出)
    const nm = Math.min(5, p.marks ?? 0);
    if (nm > 0) {
      g.fillStyle = 'rgba(110,70,40,0.35)';
      for (let i = 0; i < nm; i++) {
        const t2 = 0.15 + hash(p.id * 7 + i) * 0.7;
        const x = q.ax + (q.bx - q.ax) * t2;
        const y = q.ay + (q.by - q.ay) * t2 - h * (0.2 + hash(p.id * 11 + i) * 0.7);
        g.fillRect(x, y, 1, Math.min(6, h * 0.12));
      }
    }
    if (p.leak) {
      g.fillStyle = 'rgba(60,56,48,0.3)';
      g.beginPath();
      g.ellipse((q.bx + q.cx) / 2, (q.by + q.cy) / 2 - h + 5, 4, 3, 0, 0, Math.PI * 2);
      g.fill();
    }
    if (leaning) g.restore();
    if ((building || finishing) && !stalled) {
      // 工地门口:一间蓝色的集装箱板房、一堆钢筋
      this.solid(p.x + 0.26, p.y + 0.83, p.x + 0.48, p.y + 0.9, 3.4, '#5f86a8', '#e8e2d6');
      this.solid(p.x + 0.55, p.y + 0.84, p.x + 0.74, p.y + 0.88, 1.1, colors.rebar);
    }
    if (p.cracked) {
      // 裂缝:左面墙上两三道细细的斜裂缝(11-美术.md:看得见,不吓人)
      g.strokeStyle = colors.bad;
      g.lineWidth = 0.9;
      const id = p.projectId ?? p.id;
      for (let i = 0; i < 3; i++) {
        const t = 0.2 + hash(id * 5 + i) * 0.6;
        const up = h * (0.15 + hash(id * 9 + i) * 0.6);
        const x = q.ax + (q.bx - q.ax) * t;
        const y = q.ay + (q.by - q.ay) * t - up;
        g.beginPath();
        g.moveTo(x, y);
        g.lineTo(x + 1.5, y - 2);
        g.lineTo(x + 0.5, y - 3.5);
        g.lineTo(x + 2.5, y - 5.5);
        g.stroke();
      }
    }
    return h;
  }

  // ---------- 矮楼的门面和楼顶 ----------

  /**
   * 店屋底层的门面:正面分两间铺面,一间一种颜色的雨篷,雨篷上挂招牌(夜里招牌亮、铺面透出暖光);侧面一间;
   * 饭馆的雨篷下挂一串灯笼。fh 是一层画多高。
   */
  private storefront(q: ReturnType<CityMap['box']>, p: Plot, fh: number, sky: Sky) {
    const g = this.g;
    const id = p.projectId ?? p.id;
    const night = sky.night;
    const dining = p.use === 'dining';
    const SIGNS = ['#c0392b', '#2e6f9e', '#d08a3a', '#3f7d4f', '#7a4e8a', '#b5553d'];
    const at = (x1: number, y1: number, x2: number, y2: number, k: number, up: number): [number, number] => [x1 + (x2 - x1) * k, y1 + (y2 - y1) * k - up];
    const bays: [number, number, number, number, number, number][] = [
      [q.ax, q.ay, q.bx, q.by, 0, 0.5],
      [q.ax, q.ay, q.bx, q.by, 0.5, 1],
      [q.bx, q.by, q.cx, q.cy, 0, 1],
    ];
    bays.forEach(([x1, y1, x2, y2, k0, k1], i) => {
      const right = i === 2;
      // 铺面的玻璃(夜里透光)
      const glow = night > 0 ? '#f3c98a' : '#55707c';
      this.poly([at(x1, y1, x2, y2, k0 + 0.06, 0), at(x1, y1, x2, y2, k1 - 0.06, 0), at(x1, y1, x2, y2, k1 - 0.06, fh * 0.62), at(x1, y1, x2, y2, k0 + 0.06, fh * 0.62)], glow, right && night <= 0 ? 0.15 : 0);
      // 雨篷
      const aw = dining ? pick(['#b5553d', '#c0392b', '#d08a3a'], hash(id * 7 + i)) : pick(AWNINGS, hash(id * 41 + i * 13));
      this.poly([at(x1, y1, x2, y2, k0 + 0.02, fh * 0.62), at(x1, y1, x2, y2, k1 - 0.02, fh * 0.62), at(x1, y1, x2, y2, k1 - 0.02, fh * 0.78), at(x1, y1, x2, y2, k0 + 0.02, fh * 0.78)], aw, right ? 0.15 : 0);
      // 招牌
      const sign = pick(SIGNS, hash(id * 53 + i * 7));
      const [s1x, s1y] = at(x1, y1, x2, y2, k0 + 0.12, fh * 0.82);
      const [s2x, s2y] = at(x1, y1, x2, y2, k1 - 0.12, fh * 0.82);
      this.poly([[s1x, s1y], [s2x, s2y], [s2x, s2y - fh * 0.3], [s1x, s1y - fh * 0.3]], sign, night > 0 ? 0 : right ? 0.15 : 0);
      g.strokeStyle = night > 0 ? 'rgba(255,240,200,0.95)' : 'rgba(255,248,236,0.75)';
      g.lineWidth = 0.8;
      g.beginPath();
      g.moveTo(s1x + (s2x - s1x) * 0.2, s1y + (s2y - s1y) * 0.2 - fh * 0.15);
      g.lineTo(s1x + (s2x - s1x) * 0.8, s1y + (s2y - s1y) * 0.8 - fh * 0.15);
      g.stroke();
      if (night > 0) {
        g.fillStyle = `rgba(255,214,140,${0.18 * night})`;
        g.beginPath();
        g.ellipse((s1x + s2x) / 2, (s1y + s2y) / 2 - fh * 0.15, Math.abs(s2x - s1x) * 0.7 + 3, fh * 0.6, 0, 0, Math.PI * 2);
        g.fill();
      }
      // 饭馆:雨篷下一串灯笼
      if (dining && !right) {
        g.fillStyle = night > 0 ? '#ffb35a' : '#c0392b';
        for (let k = 0; k < 3; k++) {
          const [lx, ly] = at(x1, y1, x2, y2, k0 + (k1 - k0) * (0.25 + k * 0.25), fh * 0.5);
          g.beginPath();
          g.arc(lx, ly, 1.3, 0, Math.PI * 2);
          g.fill();
        }
      }
    });
    // 两间铺面之间的隔墙
    const [mx, my] = at(q.ax, q.ay, q.bx, q.by, 0.5, 0);
    g.strokeStyle = 'rgba(0,0,0,0.25)';
    g.lineWidth = 0.8;
    g.beginPath();
    g.moveTo(mx, my);
    g.lineTo(mx, my - fh);
    g.stroke();
  }

  /** 平顶的楼顶:沿边一圈女儿墙(楼顶面往里收一点、压暗),再按楼号放空调外机、水箱、太阳能板。坐标在地面上算,抬高 h。 */
  private flatRoof(p: Plot, inset: number, h: number) {
    const g = this.g;
    const id = p.projectId ?? p.id;
    const { x, y } = p;
    const i2 = inset + 0.03;
    this.poly([this.lift(x + i2, y + 1 - i2, h), this.lift(x + 1 - i2, y + 1 - i2, h), this.lift(x + 1 - i2, y + i2, h), this.lift(x + i2, y + i2, h)], 'rgba(0,0,0,0.08)');
    g.save();
    g.translate(0, -h);
    const r = hash(id * 71 + 3);
    if (r < 0.4) {
      // 空调外机两台
      this.solid(x + inset + 0.08, y + inset + 0.08, x + inset + 0.16, y + inset + 0.14, 2.6, '#d9d6cf', '#eeebe5');
      this.solid(x + inset + 0.22, y + inset + 0.08, x + inset + 0.3, y + inset + 0.14, 2.6, '#d9d6cf', '#eeebe5');
    } else if (r < 0.7) {
      // 水箱
      this.cylinder(x + 1 - inset - 0.12, y + inset + 0.12, 0.05, 6, '#c9c3b8', '#a8a49d');
    } else if (r < 0.85) {
      // 太阳能板
      this.quad(x + inset + 0.06, y + inset + 0.08, x + inset + 0.3, y + inset + 0.26);
      g.fillStyle = '#3d4f66';
      g.fill();
    }
    g.restore();
  }

  // ---------- 厂房 ----------

  /** 世界坐标 [xa, xb] × [ya, yb] 上立一块 h 高的盒子(左面亮、右面暗、顶面),返回四个角(和 box 一样)。 */
  private slab(xa: number, ya: number, xb: number, yb: number, h: number, wall: string, top?: string): ReturnType<CityMap['box']> {
    this.solid(xa, ya, xb, yb, h, wall, top);
    const [ax, ay] = this.iso(xa, yb);
    const [bx, by] = this.iso(xb, yb);
    const [cx, cy] = this.iso(xb, ya);
    const [dx, dy] = this.iso(xa, ya);
    return { ax, ay, bx, by, cx, cy, dx, dy };
  }

  /** 世界坐标 (x, y) 抬高 z 像素在屏幕上的位置。 */
  private lift(x: number, y: number, z: number): [number, number] {
    const [sx, sy] = this.iso(x, y);
    return [sx, sy - z];
  }

  /**
   * 双坡屋顶,盖在世界坐标 [xa, xb] × [ya, yb]、h 像素高的楼顶上,屋脊高出 rise 像素;alongX 为真屋脊顺着 x。
   * 光从左上来:朝左下的坡亮、朝右下的坡暗;露出来的山墙是墙色压暗一点。
   */
  private gable(xa: number, ya: number, xb: number, yb: number, h: number, rise: number, roof: string, wall: string, alongX: boolean) {
    const P = (x: number, y: number, z: number) => this.lift(x, y, z);
    if (alongX) {
      const ym = (ya + yb) / 2;
      const A = P(xa, yb, h), B = P(xb, yb, h), C = P(xb, ya, h), D = P(xa, ya, h), R1 = P(xa, ym, h + rise), R2 = P(xb, ym, h + rise);
      this.poly([D, C, R2, R1], roof, 0.3);
      this.poly([B, C, R2], wall, 0.24);
      this.poly([A, B, R2, R1], roof);
    } else {
      const xm = (xa + xb) / 2;
      const A = P(xa, yb, h), B = P(xb, yb, h), C = P(xb, ya, h), D = P(xa, ya, h), R1 = P(xm, ya, h + rise), R2 = P(xm, yb, h + rise);
      this.poly([D, A, R2, R1], roof, 0.05);
      this.poly([B, C, R1, R2], roof, 0.24);
      this.poly([A, B, R2], wall);
    }
  }

  /** 一块多边形:fill 上色,dark 压暗几成(光从左上来,朝右的面暗)。 */
  private poly(pts: [number, number][], fill: string, dark = 0) {
    const g = this.g;
    g.beginPath();
    g.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1]);
    g.closePath();
    g.fillStyle = fill;
    g.fill();
    if (dark) {
      g.fillStyle = `rgba(0,0,0,${dark})`;
      g.fill();
    }
  }

  /** 圆柱(料仓、储罐):底面圆心在世界坐标 (wx, wy),半径 r(格),高 hh 像素;左亮右暗,顶上一个矮锥。 */
  private cylinder(wx: number, wy: number, r: number, hh: number, color: string, cone: string) {
    const g = this.g;
    const [sx, sy] = this.iso(wx, wy);
    const rx = (r * TW) / Math.SQRT2, ry = (r * TH) / Math.SQRT2;
    g.beginPath();
    g.moveTo(sx - rx, sy - hh);
    g.lineTo(sx - rx, sy);
    g.ellipse(sx, sy, rx, ry, 0, Math.PI, 0, true);
    g.lineTo(sx + rx, sy - hh);
    g.closePath();
    g.fillStyle = color;
    g.fill();
    g.save();
    g.clip();
    g.fillStyle = 'rgba(0,0,0,0.2)';
    g.fillRect(sx + rx * 0.15, sy - hh - ry, rx, hh + ry * 2);
    g.fillStyle = 'rgba(255,255,255,0.18)';
    g.fillRect(sx - rx * 0.75, sy - hh - ry, rx * 0.25, hh + ry * 2);
    g.restore();
    // 两道箍
    g.strokeStyle = 'rgba(0,0,0,0.18)';
    g.lineWidth = 0.7;
    for (const k of [0.35, 0.7]) {
      g.beginPath();
      g.ellipse(sx, sy - hh * k, rx, ry, 0, 0, Math.PI, false);
      g.stroke();
    }
    g.beginPath();
    g.ellipse(sx, sy - hh, rx, ry, 0, 0, Math.PI * 2);
    g.fillStyle = cone;
    g.fill();
    g.beginPath();
    g.moveTo(sx - rx, sy - hh);
    g.lineTo(sx, sy - hh - ry - 4);
    g.lineTo(sx + rx, sy - hh);
    g.closePath();
    g.fillStyle = cone;
    g.fill();
    g.fillStyle = 'rgba(0,0,0,0.15)';
    g.beginPath();
    g.moveTo(sx, sy - hh - ry - 4);
    g.lineTo(sx + rx, sy - hh);
    g.lineTo(sx, sy - hh + ry * 0.3);
    g.closePath();
    g.fill();
  }

  /** 一堆砂石:锥形,底下一圈影子。 */
  private heap(wx: number, wy: number, r: number, hh: number, color: string) {
    const g = this.g;
    const [sx, sy] = this.iso(wx, wy);
    const rx = (r * TW) / Math.SQRT2, ry = (r * TH) / Math.SQRT2;
    g.beginPath();
    g.ellipse(sx, sy, rx, ry, 0, 0, Math.PI * 2);
    g.fillStyle = 'rgba(0,0,0,0.12)';
    g.fill();
    this.poly([[sx - rx, sy], [sx, sy - hh], [sx + rx, sy]], color);
    this.poly([[sx, sy - hh], [sx + rx, sy], [sx + rx * 0.2, sy + ry * 0.6]], color, 0.18);
  }

  /** 一辆货车:车头、货箱(alongX 为真车身顺着 x)。 */
  private truck(wx: number, wy: number, color: string, alongX: boolean) {
    const L = 0.16, W = 0.055;
    if (alongX) {
      this.solid(wx, wy, wx + L * 0.72, wy + W, 5, color, color);
      this.solid(wx + L * 0.74, wy, wx + L, wy + W, 3.6, '#e8e2d6', '#f2eee6');
    } else {
      this.solid(wx, wy, wx + W, wy + L * 0.72, 5, color, color);
      this.solid(wx, wy + L * 0.74, wx + W, wy + L, 3.6, '#e8e2d6', '#f2eee6');
    }
  }

  /**
   * 厂房(单机版引擎的楼型,见 rules/engine_types_v1.json):低矮的铁皮大厂房,按里面主要是谁画——
   * 工厂锯齿天窗加冒烟的烟囱,建材厂平顶加两个料仓、传送带和砂石堆,物流仓库浅坡顶、一排卷帘门、门口停货车;
   * 空着的按仓库画。门口一块水泥场地,夜里高窗亮几扇。返回厂房那一截的盒子(画裂缝、记号用)。
   */
  private works(p: Plot, sky: Sky, t: number): ReturnType<CityMap['box']> {
    const g = this.g;
    const id = p.projectId ?? p.id;
    const use = p.use === 'factory' || p.use === 'plant' ? p.use : 'depot';
    const night = sky.night;
    const h = Math.max(1, p.floors) * FH * 1.6;
    const { x, y } = p;
    // 场地
    this.quad(x + 0.05, y + 0.06, x + 0.95, y + 0.95);
    g.fillStyle = night > 0.5 ? '#77736c' : '#bdb7ad';
    g.fill();
    const plant = use === 'plant';
    const [xa, ya, xb, yb] = plant ? [x + 0.08, y + 0.1, x + 0.6, y + 0.64] : [x + 0.08, y + 0.1, x + 0.92, y + 0.66];
    const wall = plant ? '#cfc9bd' : use === 'factory' ? '#b7c2c6' : '#d8cfbe';
    const roof = use === 'factory' ? '#9aa1a4' : plant ? '#b3aea4' : '#8f979b';
    const q = this.slab(xa, ya, xb, yb, h, wall, roof);
    // 铁皮的竖棱(两面)
    g.strokeStyle = 'rgba(0,0,0,0.09)';
    g.lineWidth = 0.6;
    for (const [x1, y1, x2, y2] of [[q.ax, q.ay, q.bx, q.by], [q.bx, q.by, q.cx, q.cy]] as [number, number, number, number][]) {
      for (let i = 1; i < 14; i++) {
        const k = i / 14;
        g.beginPath();
        g.moveTo(x1 + (x2 - x1) * k, y1 + (y2 - y1) * k);
        g.lineTo(x1 + (x2 - x1) * k, y1 + (y2 - y1) * k - h);
        g.stroke();
      }
    }
    // 高窗:左面靠上一排
    for (let i = 0; i < 6; i++) {
      const k0 = (i + 0.25) / 6, k1 = (i + 0.75) / 6;
      const on = night > 0 && hash(id * 37 + i) < 0.25 + night * 0.4;
      g.strokeStyle = on ? '#ffd98f' : '#5a6e7a';
      g.lineWidth = 1.6;
      g.beginPath();
      g.moveTo(q.ax + (q.bx - q.ax) * k0, q.ay + (q.by - q.ay) * k0 - h * 0.78);
      g.lineTo(q.ax + (q.bx - q.ax) * k1, q.ay + (q.by - q.ay) * k1 - h * 0.78);
      g.stroke();
    }
    if (use === 'factory') {
      // 锯齿天窗:顺着 x 三个齿,斜面朝左上(亮),竖着的玻璃朝右(暗;夜里透光)
      const r = 5;
      const n = 3;
      for (let i = 0; i < n; i++) {
        const x0 = xa + ((xb - xa) * i) / n, x1 = xa + ((xb - xa) * (i + 1)) / n;
        this.poly([this.lift(x0, ya, h), this.lift(x0, yb, h), this.lift(x1, yb, h + r), this.lift(x1, ya, h + r)], roof);
        this.poly([this.lift(x1, ya, h), this.lift(x1, yb, h), this.lift(x1, yb, h + r), this.lift(x1, ya, h + r)], night > 0 ? '#e9c77e' : '#5f7c92', night > 0 ? 0 : 0.15);
      }
      // 砖烟囱和烟
      const cxw = xb - 0.1, cyw = ya + 0.04;
      this.solid(cxw, cyw, cxw + 0.06, cyw + 0.06, h + 26, '#a85a3c', '#7d3f2a');
      const [sx, sy] = this.lift(cxw + 0.03, cyw + 0.03, h + 26);
      g.fillStyle = 'rgba(255,255,255,0.75)';
      g.fillRect(sx - 2.2, sy + 2, 4.4, 1.6);
      for (let k = 0; k < 3; k++) {
        const ph = (t / 2400 + k / 3 + hash(id)) % 1;
        g.fillStyle = night > 0.5 ? `rgba(150,150,150,${0.3 * (1 - ph)})` : `rgba(236,232,224,${0.55 * (1 - ph)})`;
        g.beginPath();
        g.arc(sx + ph * 14, sy - 3 - ph * 16, 2.6 + ph * 3.4, 0, Math.PI * 2);
        g.fill();
      }
    } else if (plant) {
      // 料仓(两个)、传送带、砂石堆
      const silo = night > 0.5 ? '#9d9a93' : '#d6d3cc';
      this.cylinder(x + 0.78, y + 0.2, 0.085, h + 22, silo, '#a9a59d');
      this.cylinder(x + 0.78, y + 0.42, 0.085, h + 18, silo, '#a9a59d');
      const [c1x, c1y] = this.lift(xb - 0.04, (ya + yb) / 2, h);
      const [c2x, c2y] = this.lift(x + 0.78, y + 0.31, h + 20);
      g.strokeStyle = '#6f6a63';
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(c1x, c1y);
      g.lineTo(c2x, c2y);
      g.stroke();
      this.heap(x + 0.26, y + 0.8, 0.1, 8, '#c9b27a');
      this.heap(x + 0.5, y + 0.82, 0.09, 7, '#9a968f');
      this.heap(x + 0.74, y + 0.78, 0.08, 6, '#b7a58a');
    } else {
      // 仓库:浅坡顶的屋脊、正面一排卷帘门、门口的货车
      g.strokeStyle = 'rgba(255,255,255,0.35)';
      g.lineWidth = 1;
      g.beginPath();
      const [r1x, r1y] = this.lift(xa, (ya + yb) / 2, h);
      const [r2x, r2y] = this.lift(xb, (ya + yb) / 2, h);
      g.moveTo(r1x, r1y);
      g.lineTo(r2x, r2y);
      g.stroke();
      for (let i = 0; i < 4; i++) {
        const k0 = (i + 0.2) / 4, k1 = (i + 0.8) / 4;
        const ax = q.ax + (q.bx - q.ax) * k0, ay = q.ay + (q.by - q.ay) * k0;
        const bx = q.ax + (q.bx - q.ax) * k1, by = q.ay + (q.by - q.ay) * k1;
        this.poly([[ax, ay], [bx, by], [bx, by - h * 0.55], [ax, ay - h * 0.55]], night > 0 && hash(id * 7 + i) < 0.4 ? '#e9c77e' : '#77736c');
      }
      const n = 1 + Math.floor(hash(id * 3) * 3);
      for (let i = 0; i < n; i++) this.truck(x + 0.14 + i * 0.26, y + 0.78, pick(['#b5553d', '#3f6e8c', '#4f8a5b', '#d08a3a', '#e8e2d6'], hash(id * 11 + i)), true);
    }
    return q;
  }

  private crane(x: number, y: number, h: number, t: number) {
    const g = this.g;
    g.strokeStyle = colors.steel;
    g.lineWidth = 1.6;
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x, y - h);
    g.stroke();
    const a = Math.sin(t / 2400) * 1.2; // 吊臂慢慢转
    const len = 26;
    g.beginPath();
    g.moveTo(x - Math.cos(a) * 8, y - h + Math.sin(a) * 4);
    g.lineTo(x + Math.cos(a) * len, y - h - Math.sin(a) * len * 0.5);
    g.stroke();
    g.strokeStyle = 'rgba(60,50,30,0.6)';
    g.lineWidth = 0.6;
    g.beginPath();
    const hx = x + Math.cos(a) * len * 0.7;
    const hy = y - h - Math.sin(a) * len * 0.35;
    g.moveTo(hx, hy);
    g.lineTo(hx, hy + 12);
    g.stroke();
  }

  private flag(x: number, y: number) {
    const g = this.g;
    g.strokeStyle = colors.steelDark;
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x, y - 12);
    g.stroke();
    g.fillStyle = colors.gold;
    g.beginPath();
    g.moveTo(x, y - 12);
    g.lineTo(x + 8, y - 9.5);
    g.lineTo(x, y - 7);
    g.closePath();
    g.fill();
  }

  private sign(x: number, y: number, text: string) {
    const g = this.g;
    g.fillStyle = colors.steelDark;
    g.fillRect(x - 1, y - 16, 2, 14);
    g.fillStyle = '#fffaf1';
    g.fillRect(x - 13, y - 26, 26, 11);
    g.strokeStyle = colors.steel;
    g.lineWidth = 1;
    g.strokeRect(x - 13, y - 26, 26, 11);
    g.fillStyle = '#2a2110';
    g.font = '600 8px system-ui';
    g.textAlign = 'center';
    g.fillText(text, x, y - 18);
    g.textAlign = 'start';
  }

  /** 机构的名牌:板房上写「临时」。 */
  private nameplate(name: string, x: number, y: number, h: number, temp: boolean) {
    const g = this.g;
    const text = temp ? `${name}(临时)` : name;
    g.font = '600 9px system-ui, -apple-system, "PingFang SC", sans-serif';
    const w = g.measureText(text).width + 8;
    g.fillStyle = temp ? 'rgba(38,34,28,0.55)' : 'rgba(38,34,28,0.82)';
    g.fillRect(x - w / 2, y - h - 22, w, 13);
    g.fillStyle = '#f3ecdd';
    g.textAlign = 'center';
    g.fillText(text, x, y - h - 12.5);
    g.textAlign = 'start';
  }

}
